import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, HostListener, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import type { MatDialogRef } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';

import { filter, take } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { v4 as uuid } from 'uuid';

import { ChatAppRailComponent } from './components/chat-app-rail/chat-app-rail.component';
import { ChatConversationComponent } from './components/chat-conversation/chat-conversation.component';
import { ChatNewConversationComponent } from './components/chat-new-conversation/chat-new-conversation.component';
import { NewConversationStartedEvent } from './components/chat-new-conversation/chat-new-conversation.component';
import { ChatSidebarComponent } from './components/chat-sidebar/chat-sidebar.component';
import { QuickMessageLauncherService, type QuickMessageRequest } from './quick-message-launcher.service';
import { CalendarScreenComponent } from './components/calendar-screen/calendar-screen.component';
import { CallsScreenComponent } from './components/calls-screen/calls-screen.component';
import { SettingsScreenComponent } from './components/settings-screen/settings-screen.component';
import { TasksScreenComponent } from './components/tasks-screen/tasks-screen.component';
import { TodoScreenComponent } from './components/todo-screen/todo-screen.component';
import { ChangePasswordModalComponent } from './components/change-password-modal/change-password-modal.component';
import { BugReportDialogComponent } from './components/bug-report-dialog/bug-report-dialog.component';
import { ChannelAddParticipantDialogComponent } from './components/channel-add-participant-dialog/channel-add-participant-dialog.component';
import { ChannelParticipantsDialogComponent, type ChannelParticipantItem } from './components/channel-participants-dialog/channel-participants-dialog.component';
import { IncomingCallModalComponent, type IncomingCallModalResult } from './components/incoming-call-modal/incoming-call-modal.component';
import { OutgoingCallModalComponent, type OutgoingCallModalResult, type OutgoingCallModalState } from './components/outgoing-call-modal/outgoing-call-modal.component';
import { RenameGroupDialogComponent } from './components/rename-group-dialog/rename-group-dialog.component';

import { ConfirmComponent } from '../../../components/core/confirm/confirm.component';
import { AuthStoreService, SnackbarService, WebPushService } from '../../../core/services';
import { WebSocketFacade } from '../../../core/facade/websocket.facade';

import { AppMenuItem, CallLogDirection, CallLogItem, CallLogStatus, ChatComposerMessage, ChatItem, ChatMention, ChatReplyPreview, ChatTaskReference, HomeMentionNotification, MessageItem, getAvatarAccent } from './home.models';
import {
  USER_AVAILABILITY_OPTIONS,
  User,
  UserAvailabilityStatus,
  UserPresenceDisplayStatus,
  getUserPresenceLabel,
  normalizeUserAttributes,
  normalizeUserAvailabilityStatus,
  toUserPresenceDisplayStatus,
} from '../../../store/user/user.models';
import { environment } from '../../../../environments/environment';
import { BRANDING, withBrandName } from '../../../core/config/branding';

import { UserGetAllRequest, UserGetOtherUserProfilePhotoFailure, UserGetOtherUserProfilePhotoRequest, UserGetOtherUserProfilePhotoSuccess, UserGetProfilePhotoFailure, UserGetProfilePhotoRequest, UserGetProfilePhotoSuccess, UserSetAvailabilityStatusRequest } from '../../../store/user/user.actions';
import { selectAllUsers, selectUser } from '../../../store/user/user.selectors';
import {
  MeetingAnswerChannelFailure,
  MeetingAnswerChannelRequest,
  MeetingAnswerChannelSuccess,
  MeetingDeclineChannelRequest,
  MeetingDismissIncomingCall,
  MeetingGetChannelStatusRequest,
  MeetingJoinChannelFailure,
  MeetingJoinChannelRequest,
  MeetingJoinChannelSuccess,
  MeetingEndChannelSuccess,
  MeetingReceiveDeclineSignal,
  MeetingReceiveEndSignal,
  MeetingReceiveIncomingCall,
  MeetingEndChannelRequest,
  MeetingReceiveLaunchSignal,
  MeetingStartChannelFailure,
  MeetingStartChannelRequest,
  MeetingStartChannelSuccess,
} from '../../../store/meeting/meeting.actions';
import type { ChannelMeetingStatus, IncomingCallSignal, MeetingCallMode } from '../../../store/meeting/meeting.models';
import { selectIncomingMeetingCall, selectMeetingLoading, selectMeetingStatusByChannelId } from '../../../store/meeting/meeting.selectors';

import { LogoutRequest, LogoutSuccess } from '../../../store/auth/auth.actions';

import { selectAllChannels } from '../../../store/channel/channel.selectors';
import { Channel, ChannelCreate, ChannelParticipants, isChannelParticipantActive } from '../../../store/channel/channel.models';
import { ChannelAddParticipantRequest, ChannelCreateRequest, ChannelCreateSuccess, ChannelFetchRequest, ChannelLeaveSuccess, ChannelMarkMessagesReadRequest, ChannelLeaveRequest, ChannelRenameRequest } from '../../../store/channel/channel.actions';
import { selectAllChats, selectChatHasMore, selectChatLoadingOlder, selectChatNextBeforeMessageId, selectChatUploadingAttachment } from '../../../store/chat/chat.selectors';
import { ChatAttachmentUploadRequest, ChatDeleteRequest, ChatEditRequest, ChatGetRequest, ChatReactionRequest } from '../../../store/chat/chat.actions';
import {
  TaskDetailFetchRequest,
  TaskItemsFetchRequest,
  TaskNotificationsClearRequest,
  TaskNotificationsFetchRequest,
  TaskNotificationsMarkAllReadRequest,
  TaskProjectsFetchRequest,
  TaskNotificationMarkReadRequest,
  TaskSprintsFetchRequest,
} from '../../../store/task/task.actions';
import type { TaskNotification, TaskProject } from '../../../store/task/task.models';
import { selectTaskNotifications, selectTaskProjects, selectTaskUnreadNotificationCount } from '../../../store/task/task.selectors';
import { TaskReferenceLoadRequest } from '../../../store/task-reference/task-reference.actions';
import type { TaskReferenceItem } from '../../../store/task-reference/task-reference.models';
import { selectTaskReferenceItems } from '../../../store/task-reference/task-reference.selectors';

import {
  receiveChannelMessage,
  receivePresenceSnapshot,
  receiveTypingStatus,
} from '../../../store/websocket/websocket.actions';

const NIL_OBJECT_ID = '000000000000000000000000';
const CALL_SESSION_STORAGE_PREFIX = 'meetrix.daily.session.';
const CHAT_SOUND_ENABLED_STORAGE_KEY = 'meetrix.chat.sound.enabled';
const CHAT_SOUND_VOLUME_STORAGE_KEY = 'meetrix.chat.sound.volume';
const GROUP_CALL_STATUS_POLL_INTERVAL_MS = 45000;
const MEETING_STATUS_MIN_REQUEST_GAP_MS = 1500;
const MEETING_STATUS_EVENT_REFRESH_DELAY_MS = 180;

type HomeNotificationItem =
  | { kind: 'task'; id: string; createdAt: string; readAt?: string | null; task: TaskNotification }
  | { kind: 'mention'; id: string; createdAt: string; readAt?: string | null; mention: HomeMentionNotification };

@Component({
  standalone: true,
  selector: 'app-home',
  imports: [
    ChatAppRailComponent,
    ChatSidebarComponent,
    ChatConversationComponent,
    ChatNewConversationComponent,
    TasksScreenComponent,
    TodoScreenComponent,
    CalendarScreenComponent,
    CallsScreenComponent,
    SettingsScreenComponent,
    CommonModule,
  ],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home {
  protected readonly brand = BRANDING;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly titleService = inject(Title);
  private readonly authStoreService = inject(AuthStoreService);
  private readonly snackbar = inject(SnackbarService);
  private readonly webPushService = inject(WebPushService);
  private readonly actions$ = inject(Actions);
  private readonly chatSocketFacade = inject(WebSocketFacade);
  private readonly quickMessageLauncher = inject(QuickMessageLauncherService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly subscribedChannelIds = new Set<string>();
  private readonly lastMarkedReadMessageByChannel = new Map<string, string>();
  private socketConnected = false;
  private objectProfileImageUrl: string | null = null;
  private loadedProfilePhotoKey: string | null = null;
  private readonly organizationUsersById = signal<Record<string, User>>({});
  private readonly userPhotoUrlsById = signal<Record<string, string | null>>({});
  private readonly taskReferenceItems = signal<TaskReferenceItem[]>([]);
  private readonly taskProjectsCache = signal<TaskProject[]>([]);
  private readonly typingUsersByChannel = signal<Record<string, Record<string, string>>>({});
  private readonly meetingStatusByChannelId = signal<Record<string, ChannelMeetingStatus>>({});
  private readonly groupCallCandidatesByChannelId = signal<Record<string, { startedByUserId: string | null; mode: MeetingCallMode }>>({});
  private readonly latestChatActivityByChannel = signal<Record<string, string>>({});
  private readonly typingExpiryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private channelUnreadRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly meetingStatusLastRequestedAt = new Map<string, number>();
  private readonly meetingStatusRefreshTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly userPhotoObjectUrls = new Map<string, string>();
  private readonly loadedUserPhotoKeys = new Map<string, string | null>();
  private readonly pendingUserPhotoLoads = new Set<string>();
  private pendingMeetingWindow: Window | null = null;
  private activeCallWindow: Window | null = null;
  private activeCallChannelId: string | null = null;
  private activeCallMonitorId: number | null = null;
  private lastIncomingCallNotificationId: string | null = null;
  private ringContext: AudioContext | null = null;
  private ringIntervalId: number | null = null;
  private ringAudio: HTMLAudioElement | null = null;
  private messageToneAudio: HTMLAudioElement | null = null;
  private audioUnlocked = false;
  private pendingRing = false;
  private incomingCallDialogRef: MatDialogRef<IncomingCallModalComponent, IncomingCallModalResult> | null = null;
  private outgoingCallDialogRef: MatDialogRef<OutgoingCallModalComponent, OutgoingCallModalResult> | null = null;
  private outgoingCallChannelId: string | null = null;
  private outgoingCallLaunchUrl: string | null = null;
  private outgoingCallLaunchMode: MeetingCallMode | null = null;
  private outgoingCallPending = false;
  private pendingCallTargetChannelId: string | null = null;
  private pendingCallTargetMode: MeetingCallMode = 'video';
  private pendingNotificationChannelId: string | null = null;
  private lastHandledIncomingCallNotificationKey: string | null = null;
  private taskPreloadedProjectId: string | null = null;

  protected readonly appMenu: AppMenuItem[] = [
    { key: 'chat', label: 'Chat', icon: 'chat' },
    { key: 'tasks', label: 'Tasks', icon: 'assignment' },
    { key: 'todo', label: 'ToDo', icon: 'checklist' },
    { key: 'calendar', label: 'Calendar', icon: 'calendar_month' },
    { key: 'calls', label: 'Calls', icon: 'call' },
    { key: 'settings', label: 'Settings', icon: 'settings' },
    { key: 'bug_report', label: 'Report', icon: 'bug_report' },
  ];
  protected readonly availabilityOptions = USER_AVAILABILITY_OPTIONS;
  protected readonly appVersion = environment.appVersion || '0.0.0';
  protected readonly chatSoundsEnabled = signal(true);
  protected readonly chatSoundVolume = signal(14);
  protected readonly menuNotificationCounts = computed<Partial<Record<AppMenuItem['key'], number>>>(() => {
    const unreadChatCount = this.chats().reduce((total, chat) => total + (chat.unread ?? 0), 0);
    const counts: Partial<Record<AppMenuItem['key'], number>> = {};

    if (unreadChatCount > 0) {
      counts.chat = unreadChatCount;
    }

    return counts;
  });

  userInfo = signal<User | null>(null);
  protected readonly currentAvailabilityStatus = signal<UserAvailabilityStatus>('available');
  protected readonly profileImageUrl = signal<string | null>(null);
  protected readonly activeTypingParticipants = signal<string[]>([]);
  protected readonly hasMoreChatMessages = signal(false);
  protected readonly isLoadingOlderMessages = signal(false);
  protected readonly isUploadingAttachment = signal(false);
  protected readonly incomingCall = signal<IncomingCallSignal | null>(null);
  protected readonly isStartingMeeting = signal(false);

  protected readonly selectedMenu = signal<AppMenuItem['key']>('chat');
  protected readonly isProfileMenuOpen = signal(false);
  protected readonly isTaskNotificationsOpen = signal(false);
  protected readonly isStatusDropdownOpen = signal(false);
  protected readonly isComposingNewChat = signal(false);
  protected readonly newChatPrefillRecipientUserId = signal<string | null>(null);
  protected readonly taskNotifications = signal<TaskNotification[]>([]);
  protected readonly mentionNotifications = signal<HomeMentionNotification[]>([]);
  protected readonly unreadTaskNotificationCount = signal(0);
  protected readonly unreadMentionNotificationCount = computed(() =>
    this.mentionNotifications().filter((notification) => !notification.readAt).length
  );
  protected readonly unreadBellNotificationCount = computed(
    () => this.unreadTaskNotificationCount() + this.unreadMentionNotificationCount()
  );
  protected readonly bellNotifications = computed<HomeNotificationItem[]>(() => {
    const taskItems: HomeNotificationItem[] = this.taskNotifications().map((notification) => ({
      kind: 'task',
      id: `task:${notification.id}`,
      createdAt: notification.created_at,
      readAt: notification.read_at,
      task: notification,
    }));
    const mentionItems: HomeNotificationItem[] = this.mentionNotifications().map((notification) => ({
      kind: 'mention',
      id: `mention:${notification.id}`,
      createdAt: notification.createdAt,
      readAt: notification.readAt,
      mention: notification,
    }));

    return [...mentionItems, ...taskItems].sort(
      (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    );
  });
  protected readonly taskNotificationTarget = signal<{ taskId: string; projectId: string } | null>(null);

  protected readonly chats = signal<ChatItem[]>([]);
  protected readonly callLogs = signal<CallLogItem[]>([]);
  protected readonly activeChannelId = signal<string | null>(null);
  protected readonly messages = signal<MessageItem[]>([]);
  protected readonly selectedChatName = signal('');
  protected readonly reactionUserNames = computed<Record<string, string>>(() => {
    const usersById = this.organizationUsersById();

    return Object.fromEntries(
      Object.entries(usersById).map(([userId, user]) => [userId, user.name?.trim() || 'User'])
    );
  });
  protected readonly chatUserProfilesById = computed<Record<string, { name: string; email: string | null; avatarUrl: string | null }>>(() => {
    const usersById = this.organizationUsersById();
    const photoUrlsById = this.userPhotoUrlsById();

    return Object.fromEntries(
      Object.entries(usersById).map(([userId, user]) => [
        userId,
        {
          name: user.name?.trim() || 'User',
          email: user.email?.trim() || null,
          avatarUrl: photoUrlsById[userId] ?? this.getStoredProfilePhotoUrl(user) ?? null,
        },
      ])
    );
  });
  protected readonly activeChannelMentionableUsers = computed<ChatMention[]>(() => {
    const activeChannelId = this.activeChannelId();
    const currentUserId = this.userInfo()?.id;
    if (!activeChannelId) {
      return [];
    }

    const channel = this.channelsData().find((candidate) => (candidate.channel_id || candidate.id) === activeChannelId);
    if (!channel?.channel_participants?.length) {
      return [];
    }

    const usersById = this.organizationUsersById();
    const seen = new Set<string>();
    const mentions: ChatMention[] = [];

    for (const participant of channel.channel_participants) {
      const userId = participant.user_id?.trim();
      if (!userId || userId === currentUserId || seen.has(userId) || !isChannelParticipantActive(participant)) {
        continue;
      }

      const fallbackName = participant.name?.trim();
      const resolvedName = usersById[userId]?.name?.trim() || fallbackName;
      if (!resolvedName) {
        continue;
      }

      mentions.push({
        userId,
        name: resolvedName,
      });
      seen.add(userId);
    }

    return mentions.sort((left, right) => left.name.localeCompare(right.name));
  });
  protected readonly activeChannelMentionableTasks = computed<ChatTaskReference[]>(() =>
    this.taskReferenceItems().map((task) => ({
      taskId: task.taskId,
      projectId: task.projectId,
      title: task.title,
      taskCode: task.taskCode?.trim().toUpperCase() || null,
      label: task.taskCode?.trim().toUpperCase() || task.title,
    })),
  );
  protected readonly activeGroupMeetingStatus = computed<ChannelMeetingStatus | null>(() => {
    const channelId = this.activeChannelId();
    if (!channelId || !this.activeChatIsGroup()) {
      return null;
    }

    return this.meetingStatusByChannelId()[channelId] ?? null;
  });
  private readonly hasActiveGroupCallCandidate = computed<boolean>(() => {
    const channelId = this.activeChannelId();
    if (!channelId || !this.activeChatIsGroup()) {
      return false;
    }

    return Boolean(this.groupCallCandidatesByChannelId()[channelId]);
  });
  private readonly channelsData = signal<Channel[]>([]);
  private readonly nextBeforeMessageId = signal<string | null>(null);

  constructor() {
    this.quickMessageLauncher.quickMessageRequests$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((request) => this.sendQuickMessage(request));

    this.chatSoundsEnabled.set(this.readChatSoundPreference());
    this.chatSoundVolume.set(this.readChatSoundVolumePreference());

    effect(() => {
      const projects = this.taskProjectsCache();
      if (this.selectedMenu() === 'tasks' || this.taskPreloadedProjectId || !projects.length) {
        return;
      }

      const projectId = projects[0]?.id?.trim();
      if (!projectId) {
        return;
      }

      this.taskPreloadedProjectId = projectId;
      this.store.dispatch(TaskSprintsFetchRequest({ projectId }));
      this.store.dispatch(TaskItemsFetchRequest({ projectId }));
    });

    this.destroyRef.onDestroy(() => {
      this.titleService.setTitle(BRANDING.name);
      this.revokeObjectProfileImage();
      this.revokeUserPhotoImages();
      this.clearTypingExpiryTimers();
      if (this.channelUnreadRefreshTimer) {
        clearTimeout(this.channelUnreadRefreshTimer);
        this.channelUnreadRefreshTimer = null;
      }
      this.meetingStatusRefreshTimers.forEach((timer) => clearTimeout(timer));
      this.meetingStatusRefreshTimers.clear();
      this.incomingCallDialogRef?.close();
      this.outgoingCallDialogRef?.close();
    });

    // Route param drives the active shell section (chat, calendar, calls, settings).
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const menu = params.get('menu');
        if (this.isValidMenuKey(menu)) {
          this.selectedMenu.set(menu);
          if (menu !== 'chat') {
            this.isComposingNewChat.set(false);
          }
        } else {
          this.router.navigate(['/home/chat']);
        }
      });

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const channelId = params.get('channel_id')?.trim() ?? '';
        const taskId = params.get('task_id')?.trim() ?? '';
        const projectId = params.get('project_id')?.trim() ?? '';
        const incomingCall = params.get('incoming_call')?.trim() === '1';
        const startedByUserId = params.get('started_by_user_id')?.trim() ?? '';
        const startedByName = params.get('started_by_name')?.trim() ?? '';
        const roomUrl = params.get('room_url')?.trim() ?? '';
        const mode = params.get('mode')?.trim() === 'audio' ? 'audio' : 'video';

        this.pendingNotificationChannelId = channelId || null;

        if (taskId && projectId) {
          this.prefetchTaskNavigationTarget(projectId, taskId);
          this.taskNotificationTarget.set({ taskId, projectId });
        } else {
          this.taskNotificationTarget.set(null);
        }

        if (channelId) {
          queueMicrotask(() => this.trySelectPendingNotificationChat());
        }

        if (incomingCall && channelId && startedByUserId) {
          const notificationKey = `${channelId}:${startedByUserId}:${mode}`;
          if (this.lastHandledIncomingCallNotificationKey !== notificationKey) {
            this.lastHandledIncomingCallNotificationKey = notificationKey;
            this.store.dispatch(MeetingReceiveIncomingCall({
              incomingCall: {
                organization_id: this.userInfo()?.organization_id ?? '',
                channel_id: channelId,
                started_by_user_id: startedByUserId,
                started_by_name: startedByName || 'Someone',
                mode,
                provider_type: '',
                room_name: '',
                room_url: roomUrl,
                created_at: new Date().toISOString(),
              },
            }));
          }

          this.clearIncomingCallQueryState();
        }
      });

    // Whenever the selected channel changes, refresh its message history from the store/API flow.
    effect(() => {
      const activeChannelId = this.activeChannelId();
      if (activeChannelId) {
        this.store.dispatch(ChatGetRequest({ payload: { channelId: activeChannelId, mode: 'replace', limit: 30 } }));
      }

      untracked(() => this.syncActiveTypingParticipants());
    });

    effect((onCleanup) => {
      const selectedMenu = this.selectedMenu();
      const activeChannelId = this.activeChannelId();
      const isGroupConversation = this.activeChatIsGroup();
      const hasActiveGroupCallCandidate = this.hasActiveGroupCallCandidate();
      const hasActiveGroupStatus = untracked(() => this.activeGroupMeetingStatus()?.active === true);

      if (
        selectedMenu !== 'chat'
        || !activeChannelId
        || !isGroupConversation
        || (!hasActiveGroupStatus && !hasActiveGroupCallCandidate)
      ) {
        return;
      }

      const refreshStatus = () => this.requestChannelMeetingStatus(activeChannelId);
      refreshStatus();

      if (typeof window === 'undefined') {
        return;
      }

      const intervalId = window.setInterval(refreshStatus, GROUP_CALL_STATUS_POLL_INTERVAL_MS);
      onCleanup(() => window.clearInterval(intervalId));
    });

    effect(() => {
      const unreadChatCount = this.chats().reduce((total, chat) => total + (chat.unread ?? 0), 0);
      const nextTitle = unreadChatCount > 0
        ? `(${unreadChatCount}) New message${unreadChatCount === 1 ? '' : 's'} - ${BRANDING.name}`
        : BRANDING.name;
      this.titleService.setTitle(nextTitle);
    });
  }

  ngOnInit(): void {
    // Keep the local user signal in sync because several chat mapping helpers depend on it.
    this.store
      .select(selectUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        this.userInfo.set(user);
        void this.webPushService.ensureSubscribed(user?.id);
        if (user?.id) {
          this.currentAvailabilityStatus.set(normalizeUserAvailabilityStatus(user.attributes ?? null));
        }
        this.syncProfileImage(user);
        if (this.channelsData().length) {
          this.syncChatsFromChannels();
        }
      });

    // Home can be destroyed/recreated when navigating to other pages (e.g. admin).
    // Use the store-backed socket connection flag so presence works immediately when coming back.
    this.chatSocketFacade.connected$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((connected) => {
        this.socketConnected = !!connected;
        this.subscribedChannelIds.clear();
        if (this.socketConnected) {
          this.subscribeToChannels();
        }
      });

    // Channels are the source for sidebar state, unread state, and last-read metadata.
    this.store
      .select(selectAllChannels)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((channels) => {
        if (!channels) {
          return;
        }

        this.channelsData.set(channels);
        this.syncChatsFromChannels();
        this.trySelectPendingNotificationChat();

        this.subscribeToChannels();
      });

    // Chat history is filtered down to the active channel before being rendered in the conversation pane.
    this.store
      .select(selectAllChats)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((chats) => {
        if (!chats) {
          this.messages.set([]);
          this.callLogs.set([]);
          return;
        }

        this.syncCallLogs(chats);
        this.syncGroupCallCandidates(chats);

        const activeChannelId = this.activeChannelId();
        const activeChannelChats = (activeChannelId
          ? chats.filter((chat) => chat.channel_id === activeChannelId)
          : chats
        ).filter((chat) => !this.isCallLogSystemMessage(chat));

        const mappedMessages: MessageItem[] = activeChannelChats.map((chat) => ({
          id: chat.id,
          channelId: chat.channel_id,
          createdAt: chat.created_at,
          attributes: chat.attributes,
          sender: this.resolveMessageSenderName(chat.sender_id, chat.name),
          messageType: chat.message_type,
          deleted: chat.message_type === 'message_deleted' || chat.attributes?.deleted === 'true',
          edited: chat.attributes?.edited === 'true',
          system: chat.message_type === 'system',
          time: this.toTimeLabel(chat.created_at),
          text: chat.message,
          attachmentName: chat.attributes?.file_name ?? null,
          attachmentUrl: chat.attributes?.file_url ?? null,
          attachmentDownloadUrl: chat.attributes?.file_download_url ?? null,
          attachmentContentType: chat.attributes?.content_type ?? null,
          attachmentSize: this.parseAttachmentSize(chat.attributes?.file_size),
          avatar: this.getInitials(this.resolveMessageSenderName(chat.sender_id, chat.name)),
          accent: getAvatarAccent(chat.sender_id || chat.channel_id || chat.name),
          own: chat.sender_id === this.userInfo()?.id,
          dayLabel: this.toDayLabel(chat.created_at),
        }));

        this.messages.set(mappedMessages);
        this.syncOutgoingCallDialogFromMessages(mappedMessages);
      });

    this.store
      .select(selectChatHasMore)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasMore) => {
        this.hasMoreChatMessages.set(hasMore);
      });

    this.store
      .select(selectChatLoadingOlder)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loadingOlder) => {
        this.isLoadingOlderMessages.set(loadingOlder);
      });

    this.store
      .select(selectChatUploadingAttachment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((uploadingAttachment) => {
        this.isUploadingAttachment.set(uploadingAttachment);
      });

    this.store
      .select(selectChatNextBeforeMessageId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((nextBeforeMessageId) => {
        this.nextBeforeMessageId.set(nextBeforeMessageId);
      });

    // User list is needed by the new-conversation composer for recipient selection.
    this.store.dispatch(UserGetAllRequest());
    this.store.dispatch(TaskProjectsFetchRequest());
    this.store.dispatch(TaskReferenceLoadRequest());

    this.store
      .select(selectAllUsers)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((users) => {
        this.syncOrganizationUsers(users);
        if (this.channelsData().length) {
          this.syncChatsFromChannels();
        }
      });

    this.store
      .select(selectTaskNotifications)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notifications) => {
        this.taskNotifications.set(Array.isArray(notifications) ? notifications : []);
      });

    this.store
      .select(selectTaskUnreadNotificationCount)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((count) => this.unreadTaskNotificationCount.set(count ?? 0));

    this.store
      .select(selectTaskProjects)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((projects) => {
        this.taskProjectsCache.set(Array.isArray(projects) ? projects : []);
      });

    this.store
      .select(selectTaskReferenceItems)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((items) => {
        this.taskReferenceItems.set(Array.isArray(items) ? items : []);
      });

    this.readIncomingMessagesFromSocket();

    // Presence snapshots come from websocket events and update online badges in the sidebar.
    this.actions$
      .pipe(
        ofType(receivePresenceSnapshot),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ snapshot }) => {
        this.applyPresenceSnapshot(snapshot);
      });

    this.actions$
      .pipe(
        ofType(receiveTypingStatus),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ message }) => {
        this.handleTypingStatus(message);
      });

    this.actions$
      .pipe(
        ofType(ChannelLeaveSuccess),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ channelId }) => {
        this.subscribedChannelIds.delete(channelId);
      });

    this.store
    .select(selectMeetingLoading)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((loading) => {
    this.isStartingMeeting.set(loading);
    });

  this.store
    .select(selectIncomingMeetingCall)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((incomingCall) => {
    this.incomingCall.set(incomingCall);
    this.maybeNotifyIncomingCall(incomingCall);
    this.syncIncomingCallDialog(incomingCall);
    if (incomingCall) {
      this.startRingtone();
    } else {
      this.stopRingtone();
    }
    });

  this.store
    .select(selectMeetingStatusByChannelId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((statuses) => {
    this.meetingStatusByChannelId.set(statuses ?? {});
    });

  this.actions$
    .pipe(
    ofType(MeetingStartChannelSuccess),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ session }) => {
    if (session?.pending_answer) {
      this.outgoingCallPending = true;
      this.openOutgoingCallDialog(session.mode, session.channel_id);
      return;
    }

    const popup = this.consumePendingMeetingWindow();
    const launchUrl = session?.launch_url;
    this.pendingCallTargetChannelId = null;
    if (!launchUrl) {
      if (popup && !popup.closed) {
        popup.close();
      }
      return;
    }

    if (popup && !popup.closed) {
      const appCallUrl = this.createCallSessionRoute(launchUrl, {
        channelId: session.channel_id,
        mode: session.mode,
      });
      popup.location.href = appCallUrl;
      this.trackActiveCallWindow(popup, session.channel_id);
      return;
    }

    this.launchCallForMode(launchUrl, session?.channel_id ?? null, session?.mode);
    });

  this.actions$
    .pipe(
    ofType(MeetingAnswerChannelSuccess),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ session }) => {
    this.outgoingCallPending = false;
    this.closeOutgoingCallDialog();
    this.launchCallForMode(session?.launch_url, session?.channel_id ?? null, session?.mode);
    });

  this.actions$
    .pipe(
    ofType(MeetingAnswerChannelFailure),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(() => {
    this.pendingCallTargetChannelId = null;
    this.pendingCallTargetMode = 'video';
    this.consumePendingMeetingWindow(true);
    });

  this.actions$
    .pipe(
    ofType(MeetingJoinChannelSuccess),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ session }) => {
    this.launchCallForMode(session?.launch_url, session?.channel_id ?? null, session?.mode);
    });

  this.actions$
    .pipe(
    ofType(MeetingJoinChannelFailure),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(() => {
    this.pendingCallTargetChannelId = null;
    this.pendingCallTargetMode = 'video';
    this.consumePendingMeetingWindow(true);
    });

  this.actions$
    .pipe(
    ofType(MeetingEndChannelSuccess),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ channelId }) => {
    this.handleRemoteCallEnded(channelId);
    if (!this.isDirectChannel(channelId)) {
      this.requestChannelMeetingStatus(channelId, { delayMs: MEETING_STATUS_EVENT_REFRESH_DELAY_MS });
    }
    });

  this.actions$
    .pipe(
    ofType(MeetingReceiveLaunchSignal),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ launch }) => {
    if (this.outgoingCallChannelId && this.outgoingCallChannelId === launch.channel_id) {
      this.outgoingCallPending = false;
      const launchMode = launch.mode ?? this.pendingCallTargetMode;
      const contactName = this.getPendingCallName() ?? 'call';
      this.preparePendingMeetingWindow(
        `Joining ${contactName}...`,
        'Opening your meeting room'
      );
      this.closeOutgoingCallDialog();
      this.pendingCallTargetChannelId = launch.channel_id;
      this.pendingCallTargetMode = launchMode;
      this.launchCallForMode(launch.launch_url, launch.channel_id, launchMode);
      return;
    }

    this.launchCallForMode(launch.launch_url, launch.channel_id, launch.mode);
    });

  this.actions$
    .pipe(
    ofType(MeetingReceiveDeclineSignal),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ channelId }) => {
    if (!this.outgoingCallChannelId || this.outgoingCallChannelId !== channelId) {
      return;
    }

    this.outgoingCallPending = false;
    this.updateOutgoingCallDialogState('declined');
    });

  this.actions$
    .pipe(
    ofType(MeetingReceiveEndSignal),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(({ channelId }) => {
    this.handleRemoteCallEnded(channelId);
    });

  this.actions$
    .pipe(
    ofType(MeetingStartChannelFailure),
    takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(() => {
    this.outgoingCallPending = false;
    this.pendingCallTargetChannelId = null;
    const popup = this.consumePendingMeetingWindow();
    if (popup && !popup.closed) {
      popup.close();
    }
    this.closeOutgoingCallDialog();
    });

    this.store.dispatch(TaskNotificationsFetchRequest());
  }

  // Handles live websocket message events after they have been dispatched into NgRx actions.
  private readIncomingMessagesFromSocket(): void {
    this.actions$
      .pipe(
        ofType(receiveChannelMessage),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ message }) => {
        // Presence events are handled through presence snapshot.
        if (message.message_type === 'user_joined' || message.message_type === 'user_left') {
          return;
        }

        this.handleOutgoingCallSocketMessage(message);
        this.handleIncomingCallTermination(message);

        const systemEvent = message.attributes?.['event']?.trim();
        if (
          message.channel_id
          && message.channel_id === this.activeChannelId()
          && !this.isDirectChannel(message.channel_id)
          && (systemEvent === 'call_started' || systemEvent === 'call_ended')
        ) {
          this.requestChannelMeetingStatus(message.channel_id, { delayMs: MEETING_STATUS_EVENT_REFRESH_DELAY_MS });
        }

        const currentUserId = this.userInfo()?.id;
        const isOwnMessage = message.sender_id === currentUserId;
        const activeChannelId = this.activeChannelId();
        const shouldAffectUnreadCount = this.shouldAffectUnreadCount(message.message_type);
        this.updateChatActivity(message.channel_id, message.created_at);

        if (!isOwnMessage && this.isMentioningCurrentUser(message.attributes)) {
          this.pushMentionNotification(message);
        }

        if (!activeChannelId || message.channel_id !== activeChannelId) {
          // Refresh unread counts only for real chat content in non-active channels.
          if (!isOwnMessage && shouldAffectUnreadCount) {
            this.playIncomingMessageTone();
            this.scheduleUnreadChannelRefresh();
          }
          return;
        }

        const mappedMessage: MessageItem = {
          id: message._id,
          channelId: message.channel_id,
          createdAt: message.created_at,
          attributes: message.attributes,
          sender: this.resolveMessageSenderName(message.sender_id, message.name),
          messageType: message.message_type,
          deleted: message.message_type === 'message_deleted' || message.attributes?.['deleted'] === 'true',
          edited: message.attributes?.['edited'] === 'true',
          system: message.message_type === 'system',
          time: this.toTimeLabel(message.created_at),
          text: message.message,
          attachmentName: message.attributes?.['file_name'] ?? null,
          attachmentUrl: message.attributes?.['file_url'] ?? null,
          attachmentDownloadUrl: message.attributes?.['file_download_url'] ?? null,
          attachmentContentType: message.attributes?.['content_type'] ?? null,
          attachmentSize: this.parseAttachmentSize(message.attributes?.['file_size']),
          avatar: this.getInitials(this.resolveMessageSenderName(message.sender_id, message.name)),
          accent: getAvatarAccent(message.sender_id || message.channel_id || message.name),
          own: message.sender_id === this.userInfo()?.id,
          dayLabel: this.toDayLabel(message.created_at),
        };

        this.clearTypingParticipant(message.channel_id, message.sender_id);

        this.messages.update((existing) => {
          const existingIndex = existing.findIndex((item) => item.id && mappedMessage.id && item.id === mappedMessage.id);
          if (existingIndex >= 0) {
            const nextMessages = [...existing];
            nextMessages[existingIndex] = mappedMessage;
            return nextMessages;
          }

          return [...existing, mappedMessage];
        });

        // Deletions are broadcast as an updated message payload; keep the existing scroll/pagination state intact
        // instead of refetching and replacing the current message list.
      });
  }

  protected onConversationReachedTop(): void {
    const activeChannelId = this.activeChannelId();
    const beforeMessageId = this.nextBeforeMessageId();

    if (!activeChannelId || !beforeMessageId || this.isLoadingOlderMessages() || !this.hasMoreChatMessages()) {
      return;
    }

    this.store.dispatch(ChatGetRequest({
      payload: {
        channelId: activeChannelId,
        beforeMessageId,
        mode: 'prepend',
        limit: 30,
      },
    }));
  }

  // Called when the conversation component reports the user reached the bottom of the active thread.
  private markLatestMessageAsRead(channelId: string, messages: MessageItem[]): void {
    const channelMessages = messages.filter((message) => message.channelId === channelId);
    // pick the last message with valid id to mark as read
    const latestMessage = [...channelMessages].reverse().find((message) => Boolean(message.id));
    if (!latestMessage?.id) {
      return;
    }
    this.markMessageAsRead(channelId, latestMessage.id);
  }

  // Dispatches mark-read to the store/backend and caches the last local value to avoid duplicate requests.
  private markMessageAsRead(channelId: string, lastReadMessageId: string): void {
    if (!lastReadMessageId) {
      return;
    }

    const lastMarkedMessageId = this.lastMarkedReadMessageByChannel.get(channelId);
    if (lastMarkedMessageId === lastReadMessageId) {
      return;
    }

    this.lastMarkedReadMessageByChannel.set(channelId, lastReadMessageId);
    this.store.dispatch(
      ChannelMarkMessagesReadRequest({
        payload: {
          channel_id: channelId,
          last_read_message_id: lastReadMessageId,
        },
      })
    );
  }

  // Subscribes the websocket client to each known chat room after channel data and socket state are ready.
  private subscribeToChannels(): void {
    if (!this.socketConnected) {
      return;
    }

    const chats = this.chats();
    if (!chats.length) {
      return;
    }

    chats.forEach((chat) => {
      const channelId = chat.channelId;

      if (!channelId) {
        return;
      }

      if (this.subscribedChannelIds.has(channelId)) {
        return;
      }

      this.chatSocketFacade.subscribe(channelId);
      this.subscribedChannelIds.add(channelId);
    });
  }

  // Maps store channel entities into the smaller chat card model used by the sidebar component.
  private syncChatsFromChannels(): void {
    const channels = this.channelsData();
    const currentUserId = this.userInfo()?.id;
    const existingChats = this.chats();
    const activeChannelId = this.activeChannelId();

    const mappedChats: ChatItem[] = channels.map((channel) => {
      const channelId = channel.channel_id || channel.id;
      const existingChat = existingChats.find((chat) => chat.channelId === channelId);
      const displayName = this.resolveChannelDisplayName(channel, currentUserId);
      const unreadFromChannel = this.resolveUnreadCountForUser(channel, currentUserId);
      const directParticipant = this.resolveDirectChatParticipant(channel, currentUserId);
      const online = existingChat?.online ?? false;
      const lastActivityAt = this.resolveChatActivityTimestamp(channel, existingChat);
      const presenceStatus = directParticipant?.user_id
        ? this.resolveUserPresenceStatus(directParticipant.user_id, online)
        : null;

      return {
        channelId,
        channelFriendlyName: channel.channel_friendly_name,
        lastActivityAt,
        name: displayName,
        isGroup: channel.channel_participants.length > 2,
        avatarUrl: this.resolveChatAvatarUrl(directParticipant?.user_id),
        avatar: this.getInitials(displayName),
        accent: getAvatarAccent(channelId || displayName),
        unread: channelId === activeChannelId ? 0 : unreadFromChannel,
        participantUserId: directParticipant?.user_id,
        online,
        presenceStatus,
        presenceLabel: presenceStatus ? getUserPresenceLabel(presenceStatus) : null,
      };
    });

    this.chats.set(this.sortChatsByLatestActivity(mappedChats));

    const activeChat = activeChannelId
      ? mappedChats.find((chat) => chat.channelId === activeChannelId)
      : null;

    if (activeChat) {
      this.selectedChatName.set(activeChat.name);
      return;
    }

    if (mappedChats.length > 0) {
      this.selectChat(mappedChats[0]);
      return;
    }

    this.activeChannelId.set(null);
    this.selectedChatName.set('');
    this.messages.set([]);
  }

  // For direct chats, show the other participant's name instead of the backend-friendly channel name.
  private resolveChannelDisplayName(channel: Channel, currentUserId?: string): string {
    if (!currentUserId || channel.channel_participants.length !== 2) {
      return channel.channel_friendly_name;
    }

    const otherParticipant = this.resolveDirectChatParticipant(channel, currentUserId);

    if (otherParticipant?.user_id) {
      const updatedUser = this.organizationUsersById()[otherParticipant.user_id];
      const updatedName = updatedUser?.name?.trim();
      if (updatedName) {
        return updatedName;
      }
    }

    return otherParticipant?.name?.trim() || channel.channel_friendly_name;
  }

  private resolveMessageSenderName(senderId: string | undefined, fallbackName: string): string {
    if (!senderId) {
      return fallbackName;
    }

    const updatedName = this.organizationUsersById()[senderId]?.name?.trim();
    if (updatedName) {
      return updatedName;
    }

    return fallbackName;
  }

  private isCallLogSystemMessage(chat: { message_type?: string; attributes?: any } | null | undefined): boolean {
    if (!chat || chat.message_type !== 'system') {
      return false;
    }

    const event = chat.attributes?.['event'];
    return typeof event === 'string' && event.startsWith('call_');
  }

  private resolveDirectChatParticipant(channel: Channel, currentUserId?: string) {
    if (!currentUserId || channel.channel_participants.length !== 2) {
      return null;
    }

    return channel.channel_participants.find((participant) => participant.user_id !== currentUserId) ?? null;
  }

  // Unread count is stored per participant on the channel entity and projected into the sidebar model here.
  private resolveUnreadCountForUser(channel: Channel, currentUserId?: string): number {
    if (!currentUserId) {
      return 0;
    }

    const currentParticipant = channel.channel_participants.find(
      (participant) => participant.user_id === currentUserId
    );

    const unreadCount = currentParticipant?.unread_count;
    return typeof unreadCount === 'number' && unreadCount > 0 ? unreadCount : 0;
  }

  private resolveChatActivityTimestamp(channel: Channel, existingChat?: ChatItem): string {
    const channelId = channel.channel_id || channel.id;
    const latestKnownActivity = this.latestChatActivityByChannel()[channelId];

    return latestKnownActivity
      || existingChat?.lastActivityAt
      || channel.updated_at
      || channel.created_at
      || new Date(0).toISOString();
  }

  private sortChatsByLatestActivity(chats: ChatItem[]): ChatItem[] {
    return [...chats].sort((left, right) => {
      const activityDelta = this.toSortableTimestamp(right.lastActivityAt) - this.toSortableTimestamp(left.lastActivityAt);
      if (activityDelta !== 0) {
        return activityDelta;
      }

      return left.name.localeCompare(right.name);
    });
  }

  private toSortableTimestamp(value?: string | null): number {
    const parsed = value ? Date.parse(value) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private updateChatActivity(channelId?: string | null, timestamp?: string | null): void {
    const normalizedChannelId = channelId?.trim();
    const normalizedTimestamp = timestamp?.trim();
    if (!normalizedChannelId || !normalizedTimestamp) {
      return;
    }

    this.latestChatActivityByChannel.update((existing) => {
      const currentTimestamp = existing[normalizedChannelId];
      if (this.toSortableTimestamp(currentTimestamp) >= this.toSortableTimestamp(normalizedTimestamp)) {
        return existing;
      }

      return {
        ...existing,
        [normalizedChannelId]: normalizedTimestamp,
      };
    });

    this.chats.update((existingChats) => {
      const nextChats = existingChats.map((chat) =>
        chat.channelId === normalizedChannelId
          ? { ...chat, lastActivityAt: normalizedTimestamp }
          : chat
      );

      return this.sortChatsByLatestActivity(nextChats);
    });
  }

  protected getProfileInitials(): string {
    return this.getInitials(this.userInfo()?.name, 'U');
  }

  private getInitials(name: string | null | undefined, fallback: string = '?'): string {
    const normalizedName = name?.trim() ?? '';
    if (!normalizedName) {
      return fallback;
    }

    return normalizedName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || fallback;
  }

  private syncProfileImage(user: User | null): void {
    const publicUrl = this.getStoredProfilePhotoUrl(user);
    if (publicUrl) {
      this.loadedProfilePhotoKey = this.getStoredProfilePhotoKey(user);
      this.revokeObjectProfileImage();
      this.profileImageUrl.set(publicUrl);
      return;
    }

    const photoKey = this.getStoredProfilePhotoKey(user);
    if (!photoKey) {
      this.loadedProfilePhotoKey = null;
      this.revokeObjectProfileImage();
      this.profileImageUrl.set(null);
      return;
    }

    if (this.loadedProfilePhotoKey === photoKey && this.profileImageUrl()) {
      return;
    }

    this.store.dispatch(UserGetProfilePhotoRequest());
    
    this.actions$.pipe(
      ofType(UserGetProfilePhotoSuccess), take(1)).subscribe((blob) => {
        this.revokeObjectProfileImage();
        this.objectProfileImageUrl = URL.createObjectURL(blob.blob);
        this.loadedProfilePhotoKey = photoKey;
        this.profileImageUrl.set(this.objectProfileImageUrl);
      });

    this.actions$.pipe(ofType(UserGetProfilePhotoFailure), take(1)).subscribe(() => {
      this.loadedProfilePhotoKey = null;
      this.revokeObjectProfileImage();
      this.profileImageUrl.set(null);
    });
  }

  private getStoredProfilePhotoUrl(user: User | null | undefined): string | null {
    const url = normalizeUserAttributes(user?.attributes ?? null)['profile_photo_url'];
    return this.toRenderablePhotoUrl(url);
  }

  private getStoredProfilePhotoKey(user: User | null | undefined): string | null {
    const key = normalizeUserAttributes(user?.attributes ?? null)['profile_photo_key'];
    return typeof key === 'string' && key.trim() ? key : null;
  }

  private revokeObjectProfileImage(): void {
    if (this.objectProfileImageUrl) {
      URL.revokeObjectURL(this.objectProfileImageUrl);
      this.objectProfileImageUrl = null;
    }
  }

  private toRenderablePhotoUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (/^(https?:|blob:|data:)/i.test(trimmed)) {
      if (/^(blob:|data:)/i.test(trimmed)) {
        return trimmed;
      }

      try {
        const candidateUrl = new URL(trimmed);
        const apiBaseUrl = new URL(environment.apiBaseUrl);
        if (candidateUrl.origin === apiBaseUrl.origin) {
          return trimmed;
        }
      } catch {
        return null;
      }

      return null;
    }

    if (trimmed.startsWith('/')) {
      return new URL(trimmed, environment.apiBaseUrl).toString();
    }

    return null;
  }

  protected activeChatAvatarUrl(): string | null {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId) {
      return null;
    }

    return this.chats().find((chat) => chat.channelId === activeChannelId)?.avatarUrl ?? null;
  }

  protected activeChatPresenceStatus(): UserPresenceDisplayStatus | null {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId || this.activeChatIsGroup()) {
      return null;
    }

    return this.chats().find((chat) => chat.channelId === activeChannelId)?.presenceStatus ?? null;
  }

  protected activeChatParticipantUserId(): string | null {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId || this.activeChatIsGroup()) {
      return null;
    }

    return this.chats().find((chat) => chat.channelId === activeChannelId)?.participantUserId ?? null;
  }

  protected activeChatEmail(): string | null {
    const userId = this.activeChatParticipantUserId();
    if (!userId) {
      return null;
    }

    return this.organizationUsersById()[userId]?.email?.trim() || null;
  }

  protected activeChatIsGroup(): boolean {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId) {
      return false;
    }

    return this.chats().find((chat) => chat.channelId === activeChannelId)?.isGroup === true;
  }

  protected activeChatCanAddParticipants(): boolean {
    return this.activeChatIsGroup();
  }

  private syncOrganizationUsers(users: User[] | null | undefined): void {
    const nextUsersById = (users ?? []).reduce<Record<string, User>>((accumulator, user) => {
      accumulator[user.id] = user;
      return accumulator;
    }, {});

    this.organizationUsersById.set(nextUsersById);

    const activeUserIds = new Set(Object.keys(nextUsersById));
    for (const userId of this.loadedUserPhotoKeys.keys()) {
      if (activeUserIds.has(userId)) {
        continue;
      }

      this.loadedUserPhotoKeys.delete(userId);
      this.pendingUserPhotoLoads.delete(userId);
      this.revokeUserPhotoImage(userId);
    }
  }

  private resolveChatAvatarUrl(userId?: string): string | null {
    if (!userId) {
      return null;
    }

    const user = this.organizationUsersById()[userId];
    if (!user) {
      return null;
    }

    const publicUrl = this.getStoredProfilePhotoUrl(user);
    if (publicUrl) {
      const previousUrl = this.userPhotoUrlsById()[userId] ?? null;
      this.loadedUserPhotoKeys.set(userId, this.getStoredProfilePhotoKey(user));
      this.revokeUserPhotoImage(userId);
      if (previousUrl !== publicUrl) {
        this.userPhotoUrlsById.update((existing) => ({
          ...existing,
          [userId]: publicUrl,
        }));
      }
      return publicUrl;
    }

    const photoKey = this.getStoredProfilePhotoKey(user);
    if (!photoKey) {
      const previousUrl = this.userPhotoUrlsById()[userId] ?? null;
      this.loadedUserPhotoKeys.set(userId, null);
      this.revokeUserPhotoImage(userId);
      if (previousUrl !== null) {
        this.userPhotoUrlsById.update((existing) => ({
          ...existing,
          [userId]: null,
        }));
      }
      return null;
    }

    const cachedUrl = this.userPhotoUrlsById()[userId] ?? null;
    if (cachedUrl && this.loadedUserPhotoKeys.get(userId) === photoKey) {
      return cachedUrl;
    }

    this.loadUserProfilePhoto(userId, photoKey);
    return cachedUrl;
  }

  private loadUserProfilePhoto(userId: string, photoKey: string): void {
    if (this.pendingUserPhotoLoads.has(userId) || this.loadedUserPhotoKeys.get(userId) === photoKey) {
      return;
    }

    this.pendingUserPhotoLoads.add(userId);

    this.store.dispatch(UserGetOtherUserProfilePhotoRequest({ userId }));

    this.actions$.pipe(
      ofType(UserGetOtherUserProfilePhotoSuccess),
      filter((action) => action.userId === userId),
      take(1)
    ).subscribe(({ blob }) => {
      this.pendingUserPhotoLoads.delete(userId);
      this.revokeUserPhotoImage(userId);

      const objectUrl = URL.createObjectURL(blob);
      this.userPhotoObjectUrls.set(userId, objectUrl);
      this.loadedUserPhotoKeys.set(userId, photoKey);
      this.userPhotoUrlsById.update((existing) => ({
        ...existing,
        [userId]: objectUrl,
      }));
      this.syncChatsFromChannels();
    });

    this.actions$.pipe(
      ofType(UserGetOtherUserProfilePhotoFailure),
      filter((action) => action.userId === userId),
      take(1)
    ).subscribe(() => {
      this.pendingUserPhotoLoads.delete(userId);
      const previousUrl = this.userPhotoUrlsById()[userId] ?? null;
      this.loadedUserPhotoKeys.set(userId, photoKey);
      this.revokeUserPhotoImage(userId);
      if (previousUrl !== null) {
        this.userPhotoUrlsById.update((existing) => ({
          ...existing,
          [userId]: null,
        }));
        this.syncChatsFromChannels();
      }
    });
  }

  private revokeUserPhotoImages(): void {
    for (const objectUrl of this.userPhotoObjectUrls.values()) {
      URL.revokeObjectURL(objectUrl);
    }

    this.userPhotoObjectUrls.clear();
  }

  private revokeUserPhotoImage(userId: string): void {
    const existingObjectUrl = this.userPhotoObjectUrls.get(userId);
    if (existingObjectUrl) {
      URL.revokeObjectURL(existingObjectUrl);
      this.userPhotoObjectUrls.delete(userId);
    }
  }

  // Called from the websocket presence snapshot subscription in ngOnInit.
  private applyPresenceSnapshot(snapshot: {
    channel_id: string;
    users: { user_id: string; is_online: boolean }[];
    online_user_ids: string[];
  }): void {
    const channelId = snapshot.channel_id;
    const currentUserId = this.userInfo()?.id;
    if (!currentUserId) {
      return;
    }

    const channel = this.channelsData().find((item) => (item.channel_id || item.id) === channelId);
    if (!channel) {
      return;
    }

    // Online bubble is shown only for direct chats (2 participants).
    if (channel.channel_participants.length !== 2) {
      this.chats.update((existingChats) =>
        existingChats.map((chat) =>
          chat.channelId === channelId ? { ...chat, online: false, presenceStatus: null, presenceLabel: null } : chat
        )
      );
      return;
    }

    const otherParticipant = channel.channel_participants.find(
      (participant) => participant.user_id !== currentUserId
    );

    if (!otherParticipant) {
      return;
    }

    const isOnline = snapshot.online_user_ids.includes(otherParticipant.user_id)
      || snapshot.users.some(
        (item) => item.user_id === otherParticipant.user_id && item.is_online
      );
    const presenceStatus = this.resolveUserPresenceStatus(otherParticipant.user_id, isOnline);

    this.chats.update((existingChats) =>
      existingChats.map((chat) =>
        chat.channelId === channelId
          ? {
              ...chat,
              online: isOnline,
              presenceStatus,
              presenceLabel: presenceStatus ? getUserPresenceLabel(presenceStatus) : null,
            }
          : chat
      )
    );
  }

  // Called by the app rail component when the left-side menu changes.
  protected onMenuSelect(menuKey: AppMenuItem['key']): void {
    if (menuKey === 'bug_report') {
      this.openBugReportDialog();
      return;
    }
    if (menuKey !== this.selectedMenu()) {
      this.isComposingNewChat.set(false);
      this.router.navigate(['/home', menuKey]);
    }
  }

  // Triggered by the sidebar and empty state CTA.
  protected onStartNewChat(): void {
    this.newChatPrefillRecipientUserId.set(null);
    this.isComposingNewChat.set(true);
  }

  // Triggered by the new-conversation component close output.
  protected onCloseNewChat(): void {
    this.newChatPrefillRecipientUserId.set(null);
    this.isComposingNewChat.set(false);
  }

  protected onToggleChatSounds(): void {
    const nextValue = !this.chatSoundsEnabled();
    this.chatSoundsEnabled.set(nextValue);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CHAT_SOUND_ENABLED_STORAGE_KEY, String(nextValue));
    }
  }

  protected onChatSoundVolumeChange(volume: number): void {
    const nextVolume = Math.min(100, Math.max(0, Math.round(volume)));
    this.chatSoundVolume.set(nextVolume);

    if (this.messageToneAudio) {
      this.messageToneAudio.volume = nextVolume / 100;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(CHAT_SOUND_VOLUME_STORAGE_KEY, String(nextVolume));
    }
  }

  // Pass-through handler for the sidebar selectChat output.
  protected onChatSelected(chat: ChatItem): void {
    this.selectChat(chat);
  }

  protected onViewActiveChannelParticipants(): void {
    const channel = this.getActiveChannel();
    if (!channel) {
      return;
    }

    const currentUserId = this.userInfo()?.id ?? null;
    const participants: ChannelParticipantItem[] = channel.channel_participants
      .filter((participant) => isChannelParticipantActive(participant))
      .map((participant) => {
        const user = this.organizationUsersById()[participant.user_id];
        const name = user?.name?.trim() || participant.name?.trim() || 'User';
        const email = user?.email?.trim() || null;

        return {
          userId: participant.user_id,
          name,
          email,
          isYou: Boolean(currentUserId && participant.user_id === currentUserId),
        };
      })
      .sort((left, right) => {
        if (left.isYou && !right.isYou) return -1;
        if (!left.isYou && right.isYou) return 1;
        return left.name.localeCompare(right.name);
      });

    this.dialog.open(ChannelParticipantsDialogComponent, {
      width: '560px',
      maxWidth: '92vw',
      data: {
        channelName: this.resolveChannelDisplayName(channel, this.userInfo()?.id),
        participants,
      },
    });
  }

  // Centralized selection flow shared by default selection, sidebar clicks, and new chat completion.
  private selectChat(chat: ChatItem): void {
    const nextChannelId = chat.channelId || null;
    if (this.activeChannelId() !== nextChannelId) {
      this.activeChannelId.set(nextChannelId);
    }
    this.selectedChatName.set(chat.name);
    this.isComposingNewChat.set(false);
  }

  private trySelectPendingNotificationChat(): void {
    const channelId = (this.pendingNotificationChannelId ?? '').trim();
    if (!channelId) {
      return;
    }

    const chat = this.chats().find((item) => item.channelId === channelId);
    if (!chat) {
      return;
    }

    this.pendingNotificationChannelId = null;
    this.selectChat(chat);
  }

  private getActiveChannel(): Channel | null {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId) {
      return null;
    }

    return this.channelsData().find((channel) => (channel.channel_id || channel.id) === activeChannelId) ?? null;
  }

  private getEligibleUsersForChannel(channel: Channel): User[] {
    const activeParticipants = new Set(
      channel.channel_participants
        .filter((participant: ChannelParticipants) => isChannelParticipantActive(participant))
        .map((participant: ChannelParticipants) => participant.user_id)
    );

    return Object.values(this.organizationUsersById())
      .filter((user) => !activeParticipants.has(user.id))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  // Passed into the conversation component so it can place the unread separator correctly.
  protected getActiveChannelLastReadMessageId(): string | null {
    const activeChannelId = this.activeChannelId();
    const currentUserId = this.userInfo()?.id;
    if (!activeChannelId) {
      return null;
    }

    const locallyMarkedMessageId = this.lastMarkedReadMessageByChannel.get(activeChannelId);
    if (locallyMarkedMessageId) {
      return locallyMarkedMessageId;
    }

    if (!currentUserId) {
      return null;
    }

    const activeChannel = this.channelsData().find(
      (channel) => (channel.channel_id || channel.id) === activeChannelId
    );
    if (!activeChannel) {
      return null;
    }

    const currentParticipant = activeChannel.channel_participants.find(
      (participant) => participant.user_id === currentUserId
    );

    return this.normalizeLastReadMessageId(currentParticipant?.last_read_message_id ?? null);
  }
  private normalizeLastReadMessageId(lastReadMessageId: string | null | undefined): string | null {
    if (!lastReadMessageId) {
      return null;
    }

    const normalized = lastReadMessageId.trim();
    if (!normalized || normalized === NIL_OBJECT_ID) {
      return null;
    }

    return normalized;
  }

  // Fired by the conversation component once the user scrolls to the bottom of the active thread.
  protected onConversationReachedBottom(): void {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId) {
      return;
    }

    this.chats.update((existingChats) =>
      existingChats.map((chat) =>
        chat.channelId === activeChannelId ? { ...chat, unread: 0 } : chat
      )
    );

    this.markLatestMessageAsRead(activeChannelId, this.messages());
  }

  protected onConversationTypingStateChange(isTyping: boolean): void {
    const channelId = this.activeChannelId();
    if (!channelId) {
      return;
    }

    this.chatSocketFacade.sendTyping(channelId, isTyping);
  }

  protected onLeaveActiveChannel(): void {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId || !this.activeChatIsGroup()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmComponent, {
      maxWidth: '420px',
      data: {
        message: 'Are you sure you want to leave this group chat?',
        title: 'Leave Group Chat',
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.store.dispatch(ChannelLeaveRequest({
        payload: {
          channel_id: activeChannelId,
        },
      }));
    });
  }

  protected onAddParticipantToActiveChannel(): void {
    const activeChannel = this.getActiveChannel();
    if (!activeChannel || activeChannel.channel_participants.length <= 2) {
      return;
    }

    const existingUsers = Object.values(this.organizationUsersById());
    if (!existingUsers.length) {
      this.store.dispatch(UserGetAllRequest());
      this.store
        .select(selectAllUsers)
        .pipe(
          filter((users): users is User[] => Array.isArray(users)),
          take(1)
        )
        .subscribe(() => {
          this.openAddParticipantDialog(activeChannel);
        });
      return;
    }

    this.openAddParticipantDialog(activeChannel);
  }

  protected onDeleteActiveMessage(messageId: string): void {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId || !messageId) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmComponent, {
      maxWidth: '420px',
      data: {
        message: 'Are you sure you want to delete this message?',
        title: 'Delete Message',
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
      if (!result) {
        return;
      }

      this.store.dispatch(ChatDeleteRequest({
        payload: {
          channel_id: activeChannelId,
          message_id: messageId,
        },
      }));
    });
  }

  protected onEditActiveMessage(payload: { messageId: string; text: string }): void {
    const activeChannelId = this.activeChannelId();
    const trimmedMessage = payload.text.trim();

    if (!activeChannelId || !payload.messageId || !trimmedMessage) {
      return;
    }

    this.store.dispatch(ChatEditRequest({
      payload: {
        channel_id: activeChannelId,
        message_id: payload.messageId,
        message: trimmedMessage,
      },
    }));
  }

  protected onRenameActiveChannel(): void {
  const activeChannel = this.getActiveChannel();
  if (!activeChannel || activeChannel.channel_participants.length <= 2) {
    return;
  }

  const currentName = activeChannel.channel_friendly_name?.trim() || this.selectedChatName();
  const dialogRef = this.dialog.open(RenameGroupDialogComponent, {
    width: '420px',
    maxWidth: 'calc(100vw - 24px)',
    autoFocus: false,
    data: {
      currentName,
    },
  });

  dialogRef.afterClosed().pipe(take(1)).subscribe((nextName?: string) => {
    const trimmedName = nextName?.trim();
    if (!trimmedName || trimmedName === currentName) {
      return;
    }

    this.store.dispatch(ChannelRenameRequest({
      payload: {
        channel_id: activeChannel.channel_id || activeChannel.id,
        channel_friendly_name: trimmedName,
      },
    }));
  });
  }

  private openAddParticipantDialog(activeChannel: Channel): void {
    const eligibleUsers = this.getEligibleUsersForChannel(activeChannel);

    const dialogRef = this.dialog.open(ChannelAddParticipantDialogComponent, {
      width: '36rem',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      data: {
        channelName: this.selectedChatName(),
        users: eligibleUsers,
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((selectedUser?: User | null) => {
      if (!selectedUser?.id) {
        return;
      }

      this.store.dispatch(ChannelAddParticipantRequest({
        payload: {
          channel_id: activeChannel.channel_id || activeChannel.id,
          user_id: selectedUser.id,
        },
      }));
    });
  }

  // Fired by the conversation component sendMessage output for existing chats.
  protected onConversationSendMessage(message: ChatComposerMessage): void {
    const channelId = this.activeChannelId();
    const text = message?.text?.trim() ?? '';
    const attachments = Array.isArray(message?.attachments)
      ? message.attachments.filter((file): file is File => file instanceof File)
      : [];
    if (!channelId || (!text && attachments.length === 0)) {
      return;
    }

    const attributes = this.buildMessageAttributes(message.replyTo, message.mentions, message.taskReferences);

    if (attachments.length) {
      const attachmentBatchId = attachments.length > 1 ? uuid() : '';
      attachments.forEach((file, index) => {
        this.store.dispatch(ChatAttachmentUploadRequest({
          payload: {
            channel_id: channelId,
            file,
            message: index === 0 ? text : '',
            attributes: {
              ...(index === 0 ? attributes : {}),
              ...(attachmentBatchId
                ? {
                    attachment_batch_id: attachmentBatchId,
                    attachment_batch_total: String(attachments.length),
                  }
                : {}),
            },
          },
        }));
      });
      return;
    }

    if (text) {
      const clientMessageId = uuid();
      this.chatSocketFacade.sendMessage(
        channelId,
        clientMessageId,
        text,
        'text',
        attributes
      );
    }
  }

  protected onToggleMessageReaction(event: { messageId: string; emoji: string }): void {
    const channelId = this.activeChannelId();
    const messageId = event?.messageId?.trim();
    const emoji = event?.emoji?.trim();
    if (!channelId || !messageId || !emoji) {
      return;
    }

    this.store.dispatch(ChatReactionRequest({
      payload: {
        channel_id: channelId,
        message_id: messageId,
        emoji,
      },
    }));
  }

  private buildMessageAttributes(
    replyTo: ChatReplyPreview | null | undefined,
    mentions: ChatMention[] | null | undefined,
    taskReferences: ChatTaskReference[] | null | undefined
  ): Record<string, string> {
    const attributes = this.buildReplyAttributes(replyTo);
    const normalizedMentions = (mentions ?? []).filter((mention) => mention.userId?.trim() && mention.name?.trim());
    const seenTaskIds = new Set<string>();
    const normalizedTaskReferences = (taskReferences ?? []).filter((reference) => {
      const taskId = reference.taskId?.trim();
      if (!taskId || seenTaskIds.has(taskId) || !reference.projectId?.trim() || !reference.label?.trim()) {
        return false;
      }

      seenTaskIds.add(taskId);
      return true;
    });

    if (normalizedMentions.length) {
      attributes['mentioned_user_ids'] = normalizedMentions.map((mention) => mention.userId.trim()).join(',');
      attributes['mentioned_users_v1'] = JSON.stringify(
        normalizedMentions.map((mention) => ({
          user_id: mention.userId.trim(),
          name: mention.name.trim(),
        }))
      );
    }

    if (normalizedTaskReferences.length) {
      attributes['referenced_task_ids'] = normalizedTaskReferences.map((reference) => reference.taskId.trim()).join(',');
      attributes['task_references_v1'] = JSON.stringify(
        normalizedTaskReferences.map((reference) => ({
          task_id: reference.taskId.trim(),
          project_id: reference.projectId.trim(),
          title: reference.title.trim(),
          task_code: reference.taskCode?.trim() || '',
          label: reference.label.trim(),
        }))
      );
    }

    return attributes;
  }

  protected onTaskReferenceOpen(reference: ChatTaskReference): void {
    const taskId = reference.taskId?.trim();
    const projectId = reference.projectId?.trim();
    if (!taskId || !projectId) {
      return;
    }

    this.prefetchTaskNavigationTarget(projectId, taskId);
    this.onMenuSelect('tasks');
    this.taskNotificationTarget.set(null);
    queueMicrotask(() => {
      this.taskNotificationTarget.set({ taskId, projectId });
    });
  }

  private prefetchTaskNavigationTarget(projectId: string | null | undefined, taskId: string | null | undefined): void {
    const normalizedProjectId = projectId?.trim();
    const normalizedTaskId = taskId?.trim();

    this.store.dispatch(TaskProjectsFetchRequest());

    if (normalizedProjectId) {
      this.store.dispatch(TaskSprintsFetchRequest({ projectId: normalizedProjectId }));
      this.store.dispatch(TaskItemsFetchRequest({ projectId: normalizedProjectId }));
    }

    if (normalizedTaskId) {
      this.store.dispatch(TaskDetailFetchRequest({ taskId: normalizedTaskId }));
    }
  }

  private buildReplyAttributes(replyTo: ChatReplyPreview | null | undefined): Record<string, string> {
    if (!replyTo?.messageId || !replyTo.sender || (!replyTo.text && !replyTo.attachmentName)) {
      return {};
    }

    const attributes: Record<string, string> = {
      reply_to_message_id: replyTo.messageId,
      reply_to_sender: replyTo.sender.trim(),
      reply_to_text: this.truncateReplyAttribute(replyTo.text),
    };

    if (replyTo.attachmentName?.trim()) {
      attributes['reply_to_attachment_name'] = this.truncateReplyAttribute(replyTo.attachmentName);
    }

    return attributes;
  }

  private truncateReplyAttribute(value: string | null | undefined): string {
  const normalized = (value ?? '').trim();
  if (!normalized) {
    return '';
  }

  return normalized.length > 160 ? `${normalized.slice(0, 157)}...` : normalized;
  }

  protected onStartCallFromLogs(event: { channelId: string; mode: MeetingCallMode }): void {
  if (!event.channelId) {
    return;
  }

  const chat = this.chats().find((item) => item.channelId === event.channelId);
  if (chat) {
    this.selectChat(chat);
  }

  this.startCallForChannel(event.channelId, event.mode);
  }

  private syncCallLogs(chats: Array<{
  id: string;
  channel_id: string;
  sender_id: string;
  name: string;
  message_type: string;
  message: string;
  attributes?: Record<string, string>;
  created_at: string;
  }>): void {
  const currentUserId = this.userInfo()?.id;
  if (!currentUserId) {
    this.callLogs.set([]);
    return;
  }

  type PendingCallSession = {
    id: string;
    channelId: string;
    startedByUserId: string;
    direction: 'incoming' | 'outgoing';
    mode: MeetingCallMode;
    startedAt: string;
    connectedAt?: string;
    name: string;
    initials: string;
    avatarUrl?: string | null;
    accent: string;
  };

  const sortedMessages = [...chats]
    .filter((chat) => chat.message_type === 'system' && typeof chat.attributes?.['event'] === 'string')
    .sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime());

  const pendingSessions = new Map<string, PendingCallSession>();
  const logs: CallLogItem[] = [];

  sortedMessages.forEach((message) => {
    const event = message.attributes?.['event'];
    const startedByUserId = message.attributes?.['started_by_user_id']?.trim();
    const channelId = message.channel_id;
    const mode = message.attributes?.['call_mode'] === 'audio' ? 'audio' : 'video';
    const sessionKey = `${channelId}:${startedByUserId || 'unknown'}:${mode}`;

    if (event === 'call_invited' || event === 'call_started') {
      const existing = pendingSessions.get(sessionKey);
      const session = existing ?? this.createPendingCallSession(channelId, startedByUserId, mode, message.created_at, currentUserId);

      if (event === 'call_started') {
        session.connectedAt = message.created_at;
      }

      pendingSessions.set(sessionKey, session);
      return;
    }

    if (event === 'call_declined') {
      const pending = pendingSessions.get(sessionKey);
      const session = pending ?? this.createPendingCallSession(channelId, startedByUserId, mode, message.created_at, currentUserId);

      logs.push({
        ...this.toCallLogBase(session),
        id: message.id || `${sessionKey}:declined:${message.created_at}`,
        status: session.direction === 'incoming' ? 'missed' : 'declined',
        when: this.formatCallLogWhen(session.startedAt),
      });
      pendingSessions.delete(sessionKey);
      return;
    }

    if (event === 'call_ended') {
      const pending = pendingSessions.get(sessionKey);
      if (!pending) {
        return;
      }

      const connectedAt = pending.connectedAt ?? pending.startedAt;
      logs.push({
        ...this.toCallLogBase(pending),
        id: message.id || `${sessionKey}:ended:${message.created_at}`,
        status: 'completed',
        when: this.formatCallLogWhen(pending.startedAt),
        duration: this.formatCallDuration(connectedAt, message.created_at),
      });
      pendingSessions.delete(sessionKey);
    }
  });

  pendingSessions.forEach((session) => {
    logs.push({
      ...this.toCallLogBase(session),
      id: `${session.id}:ringing`,
      status: 'ringing',
      when: this.formatCallLogWhen(session.startedAt),
    });
  });

  this.callLogs.set(
    logs.sort((left, right) => new Date(right.startedAt).getTime() - new Date(left.startedAt).getTime())
  );
  }

  private syncGroupCallCandidates(chats: Array<{
  channel_id: string;
  message_type: string;
  attributes?: Record<string, string>;
  created_at: string;
  }>): void {
  const sortedMessages = [...chats]
    .filter((chat) => chat.message_type === 'system' && typeof chat.attributes?.['event'] === 'string')
    .sort((left, right) => new Date(left.created_at).getTime() - new Date(right.created_at).getTime());

  const nextCandidates: Record<string, { startedByUserId: string | null; mode: MeetingCallMode }> = {};

  sortedMessages.forEach((message) => {
    const event = message.attributes?.['event'];
    const channelId = message.channel_id?.trim();
    if (!channelId) {
      return;
    }

    if (event === 'call_started') {
      nextCandidates[channelId] = {
        startedByUserId: message.attributes?.['started_by_user_id']?.trim() || null,
        mode: message.attributes?.['call_mode'] === 'audio' ? 'audio' : 'video',
      };
      return;
    }

    if (event === 'call_ended') {
      delete nextCandidates[channelId];
    }
  });

  this.groupCallCandidatesByChannelId.set(nextCandidates);
  }

  private createPendingCallSession(
  channelId: string,
  startedByUserId: string | undefined,
  mode: MeetingCallMode,
  startedAt: string,
  currentUserId: string
  ): {
  id: string;
  channelId: string;
  startedByUserId: string;
  direction: 'incoming' | 'outgoing';
  mode: MeetingCallMode;
  startedAt: string;
  connectedAt?: string;
  name: string;
  initials: string;
  avatarUrl?: string | null;
  accent: string;
  } {
  const channel = this.channelsData().find((item) => (item.channel_id || item.id) === channelId) ?? null;
  const channelName = channel ? this.resolveChannelDisplayName(channel, currentUserId) : 'Conversation';
  const directParticipant = channel ? this.resolveDirectChatParticipant(channel, currentUserId) : null;

  return {
    id: `${channelId}:${startedByUserId || 'unknown'}:${startedAt}`,
    channelId,
    startedByUserId: startedByUserId || '',
    direction: startedByUserId === currentUserId ? 'outgoing' : 'incoming',
    mode,
    startedAt,
    name: channelName,
    initials: channelName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || 'CL',
    avatarUrl: directParticipant?.user_id ? this.resolveChatAvatarUrl(directParticipant.user_id) : null,
    accent: getAvatarAccent(channelId || channelName),
  };
  }

  private toCallLogBase(session: {
  channelId: string;
  name: string;
  initials: string;
  avatarUrl?: string | null;
  accent: string;
  direction: 'incoming' | 'outgoing';
  mode: MeetingCallMode;
  startedAt: string;
  }): Omit<CallLogItem, 'id' | 'status' | 'when'> {
  return {
    channelId: session.channelId,
    name: session.name,
    initials: session.initials,
    avatarUrl: session.avatarUrl ?? null,
    accent: session.accent,
    direction: session.direction,
    mode: session.mode,
    startedAt: session.startedAt,
  };
  }

  private formatCallLogWhen(dateLike: string): string {
  const value = new Date(dateLike);
  if (Number.isNaN(value.getTime())) {
    return '';
  }

  const now = new Date();
  const todayLabel = value.toLocaleDateString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: this.getUserTimeZone(),
  });
  const nowLabel = now.toLocaleDateString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: this.getUserTimeZone(),
  });

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayLabel = yesterday.toLocaleDateString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: this.getUserTimeZone(),
  });

  if (todayLabel === nowLabel) {
    return `Today, ${this.toTimeLabel(dateLike)}`;
  }
  if (todayLabel === yesterdayLabel) {
    return `Yesterday, ${this.toTimeLabel(dateLike)}`;
  }

  return `${this.toDayLabel(dateLike)}, ${this.toTimeLabel(dateLike)}`;
  }

  private formatCallDuration(startedAt: string, endedAt: string): string | undefined {
  const startedMs = new Date(startedAt).getTime();
  const endedMs = new Date(endedAt).getTime();
  if (!Number.isFinite(startedMs) || !Number.isFinite(endedMs) || endedMs <= startedMs) {
    return undefined;
  }

  const totalSeconds = Math.max(0, Math.floor((endedMs - startedMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
  }

  protected onStartActiveCall(mode: MeetingCallMode): void {
  const activeChannelId = this.activeChannelId();
  if (!activeChannelId || this.isStartingMeeting()) {
    return;
  }

  this.startCallForChannel(activeChannelId, mode);
  }

  private startCallForChannel(channelId: string, mode: MeetingCallMode): void {
  if (!channelId || this.isStartingMeeting()) {
    return;
  }

  this.pendingCallTargetChannelId = channelId;
  this.pendingCallTargetMode = mode;

  if (!this.isDirectChannel(channelId)) {
    this.preparePendingMeetingWindow(
      this.isDirectChannel(channelId) ? this.getPendingCallTitle() : 'Starting call',
      this.isDirectChannel(channelId) ? this.getPendingCallSubtitle() : 'Preparing your meeting room'
    );
  }

  this.store.dispatch(MeetingStartChannelRequest({
    payload: {
      channel_id: channelId,
      mode,
    },
  }));
  }

  protected onJoinCall(callUrl: string): void {
  if (!callUrl) {
    return;
  }

  const activeChannelId = this.activeChannelId();
  if (activeChannelId && !this.isDirectChannel(activeChannelId)) {
    this.pendingCallTargetChannelId = activeChannelId;
    this.pendingCallTargetMode = 'video';
    this.preparePendingMeetingWindow(
      `Joining ${this.getPendingCallName() || 'call'}...`,
      'Opening your meeting room'
    );
    this.store.dispatch(MeetingJoinChannelRequest({
      payload: {
        channel_id: activeChannelId,
      },
    }));
    return;
  }

  const callWindow = this.openTrackedCallWindow(callUrl);
  if (this.activeChannelId()) {
    this.trackActiveCallWindow(callWindow, this.activeChannelId()!);
  }
  }

  protected onEndActiveCall(): void {
  const activeChannelId = this.activeChannelId();
  if (!activeChannelId) {
    return;
  }

  this.store.dispatch(MeetingEndChannelRequest({
    payload: {
      channel_id: activeChannelId,
    },
  }));
  }

  protected dismissIncomingCall(): void {
  const incomingCall = this.incomingCall();
  this.stopRingtone();
  this.store.dispatch(MeetingDismissIncomingCall());

  if (incomingCall && this.isDirectChannel(incomingCall.channel_id)) {
    this.store.dispatch(MeetingDeclineChannelRequest({
      payload: {
        channel_id: incomingCall.channel_id,
        started_by_user_id: incomingCall.started_by_user_id,
      },
    }));
  }
  }

  protected answerIncomingCall(): void {
  const incomingCall = this.incomingCall();
  if (!incomingCall) {
    return;
  }
  this.pendingCallTargetChannelId = incomingCall.channel_id;
  this.pendingCallTargetMode = incomingCall.mode;
  this.stopRingtone();

  const chat = this.chats().find((item) => item.channelId === incomingCall.channel_id);
  if (chat) {
    this.selectChat(chat);
  }

  this.preparePendingMeetingWindow(
    `Joining ${incomingCall.started_by_name || this.getIncomingCallChannelName() || 'call'}...`,
    'Opening your meeting room'
  );

  if (this.isDirectChannel(incomingCall.channel_id)) {
    if (!incomingCall.room_url) {
      this.router.navigate(['/home/chat']);
      this.store.dispatch(MeetingDismissIncomingCall());
      this.store.dispatch(MeetingAnswerChannelRequest({
        payload: {
          channel_id: incomingCall.channel_id,
          mode: incomingCall.mode,
          started_by_user_id: incomingCall.started_by_user_id,
        },
      }));
      return;
    }

    this.router.navigate(['/home/chat']);
    this.store.dispatch(MeetingDismissIncomingCall());
    this.launchCallForMode(incomingCall.room_url, incomingCall.channel_id, incomingCall.mode);
    return;
  }

  this.router.navigate(['/home/chat']);
  this.store.dispatch(MeetingDismissIncomingCall());

  if (this.hasMeetingLaunchToken(incomingCall.room_url)) {
    this.launchCallForMode(incomingCall.room_url, incomingCall.channel_id, incomingCall.mode);
    return;
  }

  this.store.dispatch(MeetingJoinChannelRequest({
      payload: {
        channel_id: incomingCall.channel_id,
      },
    }));
  }

  @HostListener('document:click')
  @HostListener('document:keydown')
  protected unlockAudio(): void {
  if (this.audioUnlocked) {
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  const unlockAudio = new Audio('phone-ring.mp3');
  unlockAudio.volume = 0;
  unlockAudio.play()
    .then(() => {
      unlockAudio.pause();
      unlockAudio.currentTime = 0;
      this.audioUnlocked = true;
      if (this.pendingRing && this.incomingCall()) {
        this.pendingRing = false;
        this.startRingtone();
      }
    })
    .catch(() => {});

  const unlockMessageTone = new Audio('/new_message.mp3');
  unlockMessageTone.volume = 0;
  unlockMessageTone.play()
    .then(() => {
      unlockMessageTone.pause();
      unlockMessageTone.currentTime = 0;
      this.audioUnlocked = true;
    })
    .catch(() => {});

  if (this.ringContext) {
    this.ringContext.resume().then(() => {
      this.audioUnlocked = true;
    }).catch(() => {});
  }
  }

  private startRingtone(): void {
  if (this.ringIntervalId !== null || this.ringAudio) {
    return;
  }

  const audio = new Audio('/phone-ring.mp3');
  audio.loop = true;
  audio.volume = 0.5;
  audio.addEventListener('error', () => {
    if (this.ringAudio === audio) {
      this.ringAudio = null;
    }
    this.pendingRing = true;
    this.startFallbackRingtone();
  });

  const tryPlay = () => {
    audio.play().then(() => {
      this.ringAudio = audio;
    }).catch(() => {
      this.ringAudio = null;
      this.pendingRing = true;
      this.startFallbackRingtone();
    });
  };

  audio.addEventListener('canplaythrough', tryPlay, { once: true });
  audio.load();
  }

  private playIncomingMessageTone(): void {
  if (typeof window === 'undefined' || !this.audioUnlocked || !this.chatSoundsEnabled()) {
    return;
  }

  if (!this.messageToneAudio) {
    this.messageToneAudio = new Audio('/new_message.mp3');
    this.messageToneAudio.preload = 'auto';
  }

  this.messageToneAudio.volume = this.chatSoundVolume() / 100;
  this.messageToneAudio.currentTime = 0;
  this.messageToneAudio.play().catch(() => {});
  }

  private readChatSoundPreference(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  return window.localStorage.getItem(CHAT_SOUND_ENABLED_STORAGE_KEY) !== 'false';
  }

  private readChatSoundVolumePreference(): number {
  if (typeof window === 'undefined') {
    return 14;
  }

  const storedValue = Number(window.localStorage.getItem(CHAT_SOUND_VOLUME_STORAGE_KEY));
  if (!Number.isFinite(storedValue)) {
    return 14;
  }

  return Math.min(100, Math.max(0, Math.round(storedValue)));
  }

  private startFallbackRingtone(): void {
  if (this.ringIntervalId !== null) {
    return;
  }

  const AudioCtor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) {
    return;
  }

  if (!this.ringContext) {
    this.ringContext = new AudioCtor();
  }

  const context = this.ringContext;
  context.resume().catch(() => {});

  const playBeep = () => {
    if (!this.ringContext) {
      return;
    }

    const oscillator = context.createOscillator();
    const gainNode = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 520;
    gainNode.gain.setValueAtTime(0.0001, context.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.03);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.6);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start();
    oscillator.stop(context.currentTime + 0.62);
  };

  playBeep();
  this.ringIntervalId = window.setInterval(playBeep, 1400);
  }

  private stopRingtone(): void {
  this.pendingRing = false;
  if (this.ringAudio) {
    this.ringAudio.pause();
    this.ringAudio.currentTime = 0;
    this.ringAudio = null;
  }
  if (this.ringIntervalId !== null) {
    window.clearInterval(this.ringIntervalId);
    this.ringIntervalId = null;
  }
  if (this.ringContext) {
    this.ringContext.suspend().catch(() => {});
  }
  }

  private maybeNotifyIncomingCall(incomingCall: IncomingCallSignal | null): void {
  if (!incomingCall) {
    return;
  }

  const notificationId = `${incomingCall.channel_id}-${incomingCall.created_at}`;
  if (this.lastIncomingCallNotificationId === notificationId) {
    return;
  }
  this.lastIncomingCallNotificationId = notificationId;

  if (typeof window === 'undefined' || !('Notification' in window)) {
    return;
  }

  const show = () => this.showIncomingCallNotification(incomingCall);

  if (Notification.permission === 'granted') {
    show();
    return;
  }

  if (Notification.permission === 'default') {
    Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          show();
        }
      })
      .catch(() => {});
  }
  }

  private showIncomingCallNotification(incomingCall: IncomingCallSignal): void {
  const caller = incomingCall.started_by_name || 'Someone';
  const channelName = this.getIncomingCallChannelName();
  const modeLabel = incomingCall.mode === 'audio' ? 'audio' : 'video';

  const notification = new Notification(`${caller} is calling`, {
    body: `Incoming ${modeLabel} call in ${channelName}`,
    icon: BRANDING.logoPath,
    tag: `incoming-call-${incomingCall.channel_id}`,
  });

  notification.onclick = () => {
    try {
      window.focus();
    } catch {}
    this.answerIncomingCall();
    notification.close();
  };
  }

  private openTrackedCallWindow(url: string, options?: {
  channelId?: string | null;
  mode?: MeetingCallMode | null;
  }): Window | null {
  const appCallUrl = this.createCallSessionRoute(url, options);
  return this.openNewCallWindow(appCallUrl);
  }

  private launchCallForMode(url: string | null | undefined, channelId: string | null, mode: MeetingCallMode | null | undefined): void {
  const resolvedMode = mode ?? this.pendingCallTargetMode;
  this.launchCallWindow(url, channelId, resolvedMode);
  }

  private createCallSessionRoute(launchUrl: string, options?: {
  channelId?: string | null;
  mode?: MeetingCallMode | null;
  }): string {
  const sessionId = uuid();
  const sessionData = {
    id: sessionId,
    channelId: options?.channelId ?? (this.pendingCallTargetChannelId || this.activeChannelId()),
    launchUrl,
    title: this.getPendingCallName() || this.selectedChatName() || withBrandName('Call'),
    mode: options?.mode ?? this.pendingCallTargetMode,
    userName: this.userInfo()?.name?.trim() || undefined,
    createdAt: new Date().toISOString(),
  };

  window.localStorage.setItem(CALL_SESSION_STORAGE_PREFIX + sessionId, JSON.stringify(sessionData));

  const basePath = `${window.location.origin}${window.location.pathname}`;
  return `${basePath}#/call/${sessionId}`;
  }

  private launchCallWindow(url: string | null | undefined, channelId: string | null, mode: MeetingCallMode): void {
  if (!url || !channelId) {
    return;
  }

  const appCallUrl = this.createCallSessionRoute(url, { channelId, mode });
  const popup = this.consumePendingMeetingWindow();
  if (popup && !popup.closed) {
    popup.location.href = appCallUrl;
    this.trackActiveCallWindow(popup, channelId);
    return;
  }

  const callWindow = this.openNewCallWindow(appCallUrl);
  if (!callWindow) {
    void this.router.navigateByUrl(this.getAppRouteFromCallUrl(appCallUrl));
    return;
  }
  this.trackActiveCallWindow(callWindow, channelId);
  }

  private consumePendingMeetingWindow(closeOnly: boolean = false): Window | null {
  const popup = this.pendingMeetingWindow;
  this.pendingMeetingWindow = null;

  if (closeOnly && popup && !popup.closed) {
    popup.close();
  }

  return closeOnly ? null : popup;
  }

  private openOutgoingCallDialog(mode: MeetingCallMode, channelId: string): void {
  this.outgoingCallChannelId = channelId;
  this.outgoingCallLaunchUrl = null;
  this.outgoingCallLaunchMode = null;

  if (this.outgoingCallDialogRef) {
    this.updateOutgoingCallDialogState('pending');
    return;
  }

  this.outgoingCallDialogRef = this.dialog.open(OutgoingCallModalComponent, {
    width: '420px',
    maxWidth: 'calc(100vw - 24px)',
    autoFocus: false,
    disableClose: true,
    panelClass: 'incoming-call-dialog-panel',
    data: {
      contactName: this.getPendingCallName() ?? 'Someone',
      avatarUrl: this.getPendingCallAvatarUrl(),
      initials: this.getPendingCallInitials(),
      mode,
    },
  });

  this.outgoingCallDialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
    const launchUrl = this.outgoingCallLaunchUrl;
    const targetChannelId = this.outgoingCallChannelId;
    const launchMode = this.outgoingCallLaunchMode ?? this.pendingCallTargetMode;
    const wasPending = this.outgoingCallPending;
    this.outgoingCallDialogRef = null;
    this.outgoingCallLaunchUrl = null;
    this.outgoingCallLaunchMode = null;
    this.outgoingCallChannelId = null;
    this.outgoingCallPending = false;

    if (result === 'join') {
      this.launchCallForMode(launchUrl, targetChannelId, launchMode);
      return;
    }

    if (result === 'cancel' && wasPending && targetChannelId) {
      this.store.dispatch(MeetingEndChannelRequest({
        payload: {
          channel_id: targetChannelId,
        },
      }));
    }
  });
  }

  private updateOutgoingCallDialogState(state: OutgoingCallModalState): void {
  queueMicrotask(() => {
    this.outgoingCallDialogRef?.componentInstance.setState(state);
  });
  }

  private closeOutgoingCallDialog(): void {
  this.outgoingCallDialogRef?.close();
  this.outgoingCallDialogRef = null;
  this.outgoingCallLaunchUrl = null;
  this.outgoingCallLaunchMode = null;
  this.outgoingCallChannelId = null;
  this.pendingCallTargetChannelId = null;
  this.pendingCallTargetMode = 'video';
  }

  private openNewCallWindow(appCallUrl: string): Window | null {
  const callWindow = window.open(appCallUrl, '_blank');
  if (callWindow && !callWindow.closed) {
    try {
      callWindow.opener = null;
    } catch {}
  }

  return callWindow;
  }

  private preparePendingMeetingWindow(title: string, message: string): void {
  if (this.pendingMeetingWindow && !this.pendingMeetingWindow.closed) {
    this.renderPendingMeetingWindow(this.pendingMeetingWindow, title, message);
    return;
  }

  this.pendingMeetingWindow = window.open('', '_blank');
  if (this.pendingMeetingWindow && !this.pendingMeetingWindow.closed) {
    this.pendingMeetingWindow.opener = null;
    this.renderPendingMeetingWindow(this.pendingMeetingWindow, title, message);
  }
  }

  private getAppRouteFromCallUrl(appCallUrl: string): string {
  try {
    const parsedUrl = new URL(appCallUrl);
    const hashRoute = parsedUrl.hash.startsWith('#') ? parsedUrl.hash.slice(1) : parsedUrl.hash;
    return hashRoute || '/home/chat';
  } catch {
    return '/home/chat';
  }
  }

  private clearIncomingCallQueryState(): void {
  void this.router.navigate([], {
    relativeTo: this.route,
    queryParams: {
      incoming_call: null,
      started_by_user_id: null,
      started_by_name: null,
      room_url: null,
      mode: null,
    },
    queryParamsHandling: 'merge',
    replaceUrl: true,
  });
  }

  private renderPendingMeetingWindow(targetWindow: Window, title: string, message: string): void {
  const avatarUrl = this.getPendingCallAvatarUrl();
  const avatarInitials = this.getPendingCallInitials();
  const avatarMarkup = avatarUrl
    ? `<img src="${this.escapeHtml(avatarUrl)}" alt="${this.escapeHtml(title)}" class="call-waiting__avatar-image" />`
    : `<span class="call-waiting__avatar-fallback">${this.escapeHtml(avatarInitials)}</span>`;

  targetWindow.document.title = title;
  targetWindow.document.body.innerHTML = `
    <style>
      :root {
        color-scheme: light dark;
        --bg:
          radial-gradient(circle at top, rgba(44, 123, 229, 0.22), transparent 42%),
          linear-gradient(160deg, #0f172a 0%, #111827 48%, #020617 100%);
        --card-bg: rgba(15, 23, 42, 0.78);
        --card-border: rgba(148, 163, 184, 0.22);
        --text: #e5eefb;
        --muted: #94a3b8;
        --accent: #38bdf8;
        --ring: rgba(56, 189, 248, 0.28);
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 0;
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 32px;
        background: var(--bg);
        color: var(--text);
        font-family: "Segoe UI", "SF Pro Display", "Helvetica Neue", sans-serif;
      }

      .call-waiting {
        width: min(100%, 420px);
        padding: 32px;
        border-radius: 28px;
        background: var(--card-bg);
        border: 1px solid var(--card-border);
        box-shadow:
          0 28px 80px rgba(15, 23, 42, 0.45),
          inset 0 1px 0 rgba(255, 255, 255, 0.06);
        backdrop-filter: blur(18px);
        text-align: center;
      }

      .call-waiting__badge {
        width: 88px;
        height: 88px;
        margin: 0 auto 24px;
        display: grid;
        place-items: center;
        border-radius: 999px;
        background:
          radial-gradient(circle, rgba(56, 189, 248, 0.3), rgba(56, 189, 248, 0.08) 60%, transparent 62%);
        box-shadow: 0 0 0 12px var(--ring);
        overflow: hidden;
      }

      .call-waiting__avatar-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .call-waiting__avatar-fallback {
        font-size: 28px;
        line-height: 1;
        font-weight: 700;
        letter-spacing: 0.08em;
        color: #f8fafc;
      }

      .call-waiting h1 {
        margin: 0 0 10px;
        font-size: 30px;
        font-weight: 700;
        letter-spacing: -0.03em;
      }

      .call-waiting p {
        margin: 0;
        color: var(--muted);
        font-size: 15px;
        line-height: 1.6;
      }

      .call-waiting__dots {
        display: inline-flex;
        gap: 8px;
        margin-top: 22px;
      }

      .call-waiting__dots span {
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background: var(--accent);
        opacity: 0.28;
        animation: pulse 1.2s infinite ease-in-out;
      }

      .call-waiting__dots span:nth-child(2) {
        animation-delay: 0.18s;
      }

      .call-waiting__dots span:nth-child(3) {
        animation-delay: 0.36s;
      }

      @keyframes pulse {
        0%, 80%, 100% {
          opacity: 0.28;
          transform: scale(0.9);
        }
        40% {
          opacity: 1;
          transform: scale(1);
        }
      }
    </style>
    <main class="call-waiting" aria-live="polite">
      <div class="call-waiting__badge" aria-hidden="true">
        ${avatarMarkup}
      </div>
      <h1>${this.escapeHtml(title)}</h1>
      <p>${this.escapeHtml(message)}</p>
      <div class="call-waiting__dots" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </div>
    </main>
  `;
  }

  private escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
  }

  private getPendingCallTitle(): string {
  const activeChatName = this.getPendingCallName();
  if (!activeChatName) {
    return 'Calling...';
  }

  return `Calling ${activeChatName}...`;
  }

  private getPendingCallSubtitle(): string {
  const activeChatName = this.getPendingCallName();
  if (!activeChatName) {
    return 'Waiting for the other person to answer';
  }

  return `Waiting for ${activeChatName} to answer`;
  }

  private getPendingCallName(): string | null {
  const channelId = this.pendingCallTargetChannelId || this.activeChannelId();
  if (!channelId) {
    return null;
  }

  const activeChatName = this.chats().find((chat) => chat.channelId === channelId)?.name?.trim();
  return activeChatName || null;
  }

  private hasMeetingLaunchToken(url: string | null | undefined): boolean {
  const trimmed = url?.trim?.() ?? '';
  if (!trimmed) {
    return false;
  }

  try {
    const parsed = new URL(trimmed);
    return parsed.searchParams.has('t');
  } catch {
    return /(?:\?|&)t=/.test(trimmed);
  }
  }

  private getPendingCallAvatarUrl(): string | null {
  const channelId = this.pendingCallTargetChannelId || this.activeChannelId();
  if (!channelId) {
    return null;
  }

  return this.chats().find((chat) => chat.channelId === channelId)?.avatarUrl ?? null;
  }

  private getPendingCallInitials(): string {
  const activeChatName = this.getPendingCallName();
  if (!activeChatName) {
    return 'CA';
  }

  return activeChatName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'CA';
  }

  private syncOutgoingCallDialogFromMessages(messages: MessageItem[]): void {
  if (!this.outgoingCallChannelId) {
    return;
  }

  const currentUserId = this.userInfo()?.id;
  if (!currentUserId) {
    return;
  }

  const latestRelevantEvent = [...messages]
    .reverse()
    .find((message) => {
      if (!message.system || message.channelId !== this.outgoingCallChannelId) {
        return false;
      }

      const event = message.attributes?.['event'];
      if (event !== 'call_declined' && event !== 'call_started' && event !== 'call_ended') {
        return false;
      }

      return message.attributes?.['started_by_user_id'] === currentUserId;
    });

  const event = latestRelevantEvent?.attributes?.['event'];
  if (event === 'call_declined' || event === 'call_ended') {
    this.outgoingCallPending = false;
    this.updateOutgoingCallDialogState('declined');
  }
  }

  private handleOutgoingCallSocketMessage(message: {
  channel_id: string;
  message_type: string;
  attributes?: Record<string, string>;
  }): void {
  if (!this.outgoingCallChannelId || message.message_type !== 'system' || message.channel_id !== this.outgoingCallChannelId) {
    return;
  }

  const currentUserId = this.userInfo()?.id;
  if (!currentUserId) {
    return;
  }

  const event = message.attributes?.['event'];
  const startedByUserId = message.attributes?.['started_by_user_id'];
  if (startedByUserId !== currentUserId) {
    return;
  }

  if (event === 'call_declined' || event === 'call_ended') {
    this.outgoingCallPending = false;
    this.updateOutgoingCallDialogState('declined');
  }
  }

  private handleIncomingCallTermination(message: {
  channel_id: string;
  message_type: string;
  attributes?: Record<string, string>;
  }): void {
  const incomingCall = this.incomingCall();
  if (!incomingCall || message.message_type !== 'system' || message.channel_id !== incomingCall.channel_id) {
    return;
  }

  const event = message.attributes?.['event'];
  if (event !== 'call_ended' && event !== 'call_declined') {
    return;
  }

  this.stopRingtone();
  this.store.dispatch(MeetingDismissIncomingCall());
  }

  private trackActiveCallWindow(callWindow: Window | null, channelId: string): void {
  if (!callWindow) {
    return;
  }

  this.activeCallWindow = callWindow;
  this.activeCallChannelId = channelId;

  if (this.activeCallMonitorId !== null) {
    window.clearInterval(this.activeCallMonitorId);
    this.activeCallMonitorId = null;
  }

  this.activeCallMonitorId = window.setInterval(() => {
    if (!this.activeCallWindow || !this.activeCallChannelId) {
      return;
    }

    if (this.activeCallWindow.closed) {
      const closedChannelId = this.activeCallChannelId;
      const shouldEndChannel = this.isDirectChannel(closedChannelId);
      this.activeCallWindow = null;
      this.activeCallChannelId = null;

      if (this.activeCallMonitorId !== null) {
        window.clearInterval(this.activeCallMonitorId);
        this.activeCallMonitorId = null;
      }

      if (shouldEndChannel) {
        this.store.dispatch(MeetingEndChannelRequest({ payload: { channel_id: closedChannelId } }));
      }
    }
  }, 1000);
  }

  private handleRemoteCallEnded(channelId: string): void {
  if (this.outgoingCallChannelId && this.outgoingCallChannelId === channelId) {
    this.outgoingCallPending = false;
    this.updateOutgoingCallDialogState('declined');
  }

  if (this.activeCallChannelId !== channelId || !this.activeCallWindow) {
    return;
  }

  const callWindow = this.activeCallWindow;
  this.activeCallWindow = null;
  this.activeCallChannelId = null;

  if (this.activeCallMonitorId !== null) {
    window.clearInterval(this.activeCallMonitorId);
    this.activeCallMonitorId = null;
  }

  try {
    callWindow.close();
  } catch {}
  }

  protected getIncomingCallChannelName(): string {
  const incomingCall = this.incomingCall();
  if (!incomingCall) {
    return '';
  }

  return this.chats().find((item) => item.channelId === incomingCall.channel_id)?.name ?? 'Conversation';
  }

  private syncIncomingCallDialog(incomingCall: IncomingCallSignal | null): void {
  if (!incomingCall) {
    this.incomingCallDialogRef?.close();
    this.incomingCallDialogRef = null;
    return;
  }

  if (this.incomingCallDialogRef) {
    return;
  }

  this.incomingCallDialogRef = this.dialog.open(IncomingCallModalComponent, {
    width: '420px',
    maxWidth: 'calc(100vw - 24px)',
    autoFocus: false,
    disableClose: true,
    panelClass: 'incoming-call-dialog-panel',
    data: {
      incomingCall,
      channelName: this.getIncomingCallChannelName(),
    },
  });

  this.incomingCallDialogRef.afterClosed().pipe(take(1)).subscribe((result) => {
    this.incomingCallDialogRef = null;

    if (result === 'answer') {
      this.answerIncomingCall();
      return;
    }

    if (result !== 'dismiss') {
      return;
    }

    this.dismissIncomingCall();
  });
  }

  private isDirectChannel(channelId: string): boolean {
  if (!channelId) {
    return false;
  }

  const channel = this.channelsData().find((candidate) => candidate.channel_id === channelId);
  if (!channel) {
    return false;
  }

  return channel.channel_participants.length === 2;
  }

  private requestChannelMeetingStatus(channelId: string, options?: { delayMs?: number }): void {
  const trimmedChannelId = channelId?.trim();
  if (!trimmedChannelId) {
    return;
  }

  const delayMs = options?.delayMs ?? 0;
  if (delayMs > 0) {
    const existingTimer = this.meetingStatusRefreshTimers.get(trimmedChannelId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    this.meetingStatusRefreshTimers.set(
      trimmedChannelId,
      setTimeout(() => {
        this.meetingStatusRefreshTimers.delete(trimmedChannelId);
        this.requestChannelMeetingStatus(trimmedChannelId);
      }, delayMs)
    );
    return;
  }

  const now = Date.now();
  const lastRequestedAt = this.meetingStatusLastRequestedAt.get(trimmedChannelId) ?? 0;
  if (now-lastRequestedAt < MEETING_STATUS_MIN_REQUEST_GAP_MS) {
    return;
  }

  this.meetingStatusLastRequestedAt.set(trimmedChannelId, now);
  this.store.dispatch(MeetingGetChannelStatusRequest({
    payload: {
      channel_id: trimmedChannelId,
    },
  }));
  }

  // Fired by the new-conversation component after it creates a channel and queues the first message.
  protected onNewConversationStarted(event: NewConversationStartedEvent): void {
    const recipientNames = event.recipients.map((recipient) => recipient.name);
    const chatName =
      recipientNames.length === 1
        ? recipientNames[0]
        : `${recipientNames[0]} +${recipientNames.length - 1}`;

    const chatAvatar =
      event.recipients.length === 1 ? event.recipients[0].initials : 'GR';

    const chatAccent = getAvatarAccent(event.channelId || recipientNames.join(','));

    const newChat: ChatItem = {
      channelId: event.channelId,
      lastActivityAt: new Date().toISOString(),
      name: chatName,
      avatar: chatAvatar,
      accent: chatAccent,
      online: event.recipients.length === 1,
      unread: 0,
    };

    this.chats.update((existingChats) => {
      const withoutExisting = existingChats.filter((chat) => chat.channelId !== event.channelId);
      return this.sortChatsByLatestActivity([
        newChat,
        ...withoutExisting,
      ]);
    });

    this.messages.set([]);
    this.selectChat(newChat);
    this.newChatPrefillRecipientUserId.set(null);
    this.isComposingNewChat.set(false);
  }

  private sendQuickMessage(request: QuickMessageRequest): void {
    const normalizedUserId = request.userId.trim();
    const message = request.message.trim();
    const currentUserId = this.userInfo()?.id?.trim() || '';
    if (!normalizedUserId || !message || normalizedUserId === currentUserId) {
      return;
    }

    const existingDirectChat = this.chats().find((chat) => (chat.participantUserId ?? '') === normalizedUserId);
    if (existingDirectChat?.channelId) {
      this.chatSocketFacade.subscribe(existingDirectChat.channelId);
      this.chatSocketFacade.sendMessage(existingDirectChat.channelId, uuid(), message, 'text');
      return;
    }

    const currentUser = this.userInfo();
    const recipient = this.organizationUsersById()[normalizedUserId];
    if (!currentUser || !recipient) {
      return;
    }

    const channelPayload: ChannelCreate = {
      channel_friendly_name: recipient.name,
      channel_participants: [
        {
          user_id: normalizedUserId,
          name: recipient.name,
          is_active_on_channel: true,
          has_left_channel: false,
          last_read_message_id: null,
          last_read_at: null,
          unread_count: 0,
        },
        {
          user_id: currentUser.id,
          name: currentUser.name,
          is_active_on_channel: true,
          has_left_channel: false,
          last_read_message_id: null,
          last_read_at: null,
          unread_count: 0,
        },
      ],
    };

    this.store.dispatch(ChannelCreateRequest({ payload: channelPayload }));

    this.actions$.pipe(
      ofType(ChannelCreateSuccess),
      filter(({ channel }) => {
        const participantIds = new Set((channel.channel_participants ?? []).map((participant) => participant.user_id));
        return participantIds.has(normalizedUserId) && participantIds.has(currentUser.id) && participantIds.size === 2;
      }),
      take(1),
    ).subscribe(({ channel }) => {
      const channelId = channel.channel_id || channel.id;
      if (!channelId) {
        return;
      }

      this.chatSocketFacade.subscribe(channelId);
      this.chatSocketFacade.sendMessage(channelId, uuid(), message, 'text');
      this.upsertDirectChatPreview(normalizedUserId, channelId);
    });
  }

  private upsertDirectChatPreview(userId: string, channelId: string): void {
    const recipient = this.organizationUsersById()[userId];
    if (!recipient) {
      return;
    }

    const presenceStatus = this.resolveUserPresenceStatus(userId, true);
    const nextChat: ChatItem = {
      channelId,
      lastActivityAt: new Date().toISOString(),
      name: recipient.name?.trim() || 'User',
      avatarUrl: this.resolveChatAvatarUrl(userId),
      avatar: this.getInitials(recipient.name?.trim() || 'User'),
      accent: getAvatarAccent(userId || channelId),
      unread: 0,
      online: true,
      participantUserId: userId,
      presenceStatus,
      presenceLabel: getUserPresenceLabel(presenceStatus),
    };

    this.chats.update((existingChats) => {
      const withoutExisting = existingChats.filter((chat) => chat.channelId !== channelId && (chat.participantUserId ?? '') !== userId);
      return this.sortChatsByLatestActivity([nextChat, ...withoutExisting]);
    });
  }

  // Profile menu is toggled from header button clicks and closed by document click handling below.
  protected toggleProfileMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isProfileMenuOpen.update((isOpen) => {
      const nextOpenState = !isOpen;
      if (!nextOpenState) {
        this.isStatusDropdownOpen.set(false);
      }
      return nextOpenState;
    });
  }

  protected onProfileMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected toggleTaskNotifications(event: MouseEvent): void {
    event.stopPropagation();
    this.isTaskNotificationsOpen.update((isOpen) => !isOpen);
  }

  protected onTaskNotificationsMenuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected markAllTaskNotificationsRead(): void {
    this.store.dispatch(TaskNotificationsMarkAllReadRequest());
  }

  protected clearTaskNotifications(): void {
    this.store.dispatch(TaskNotificationsClearRequest());
    this.mentionNotifications.set([]);
  }

  protected openTaskNotification(notification: TaskNotification): void {
    this.prefetchTaskNavigationTarget(notification.project_id, notification.task_id);
    this.store.dispatch(TaskNotificationMarkReadRequest({ notificationId: notification.id }));
    this.isTaskNotificationsOpen.set(false);
    this.onMenuSelect('tasks');
    this.taskNotificationTarget.set(null);
    queueMicrotask(() => {
      this.taskNotificationTarget.set({ taskId: notification.task_id, projectId: notification.project_id });
    });
  }

  protected openBellNotification(notification: HomeNotificationItem): void {
    if (notification.kind === 'task') {
      this.openTaskNotification(notification.task);
      return;
    }

    this.markMentionNotificationRead(notification.mention.id);
    this.isTaskNotificationsOpen.set(false);
    this.onMenuSelect('chat');
    const chat = this.chats().find((item) => item.channelId === notification.mention.channelId);
    if (chat) {
      this.selectChat(chat);
    } else {
      this.pendingNotificationChannelId = notification.mention.channelId;
    }
  }

  protected markAllBellNotificationsRead(): void {
    this.markAllMentionNotificationsRead();
    this.store.dispatch(TaskNotificationsMarkAllReadRequest());
  }

  protected clearBellNotifications(): void {
    this.clearTaskNotifications();
  }

  protected getBellNotificationTitle(notification: HomeNotificationItem): string {
    return notification.kind === 'task' ? notification.task.title : notification.mention.title;
  }

  protected getBellNotificationBody(notification: HomeNotificationItem): string {
    return notification.kind === 'task' ? notification.task.body : notification.mention.body;
  }

  protected getBellNotificationTimestamp(notification: HomeNotificationItem): string {
    return notification.kind === 'task'
      ? this.formatTaskNotificationTime(notification.task.created_at)
      : this.formatTaskNotificationTime(notification.mention.createdAt);
  }

  protected isBellNotificationUnread(notification: HomeNotificationItem): boolean {
    return !(notification.kind === 'task' ? notification.task.read_at : notification.mention.readAt);
  }

  protected isMentionBellNotification(notification: HomeNotificationItem): boolean {
    return notification.kind === 'mention';
  }

  protected formatTaskNotificationTime(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleString();
  }

  private isMentioningCurrentUser(attributes: Record<string, string> | undefined): boolean {
    const currentUserId = this.userInfo()?.id?.trim();
    if (!currentUserId) {
      return false;
    }

    const mentionedIds = (attributes?.['mentioned_user_ids'] ?? '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return mentionedIds.includes(currentUserId);
  }

  private pushMentionNotification(message: {
    _id: string;
    channel_id: string;
    sender_id: string;
    name: string;
    message: string;
    created_at: string;
  }): void {
    const messageId = message._id?.trim();
    const channelId = message.channel_id?.trim();
    if (!messageId || !channelId) {
      return;
    }

    const sender = this.resolveMessageSenderName(message.sender_id, message.name);
    const body = message.message?.trim() || `${sender} mentioned you`;
    const notification: HomeMentionNotification = {
      id: `${messageId}:${channelId}`,
      channelId,
      messageId,
      sender,
      title: `${sender} mentioned you`,
      body,
      createdAt: message.created_at,
      readAt: null,
    };

    this.mentionNotifications.update((existing) => {
      const existingIndex = existing.findIndex((item) => item.id === notification.id);
      if (existingIndex >= 0) {
        const next = [...existing];
        next[existingIndex] = {
          ...next[existingIndex],
          ...notification,
          readAt: next[existingIndex].readAt,
        };
        return next;
      }

      return [notification, ...existing].slice(0, 100);
    });
  }

  private markMentionNotificationRead(notificationId: string): void {
    const readAt = new Date().toISOString();
    this.mentionNotifications.update((existing) =>
      existing.map((item) => (item.id === notificationId ? { ...item, readAt } : item))
    );
  }

  private markAllMentionNotificationsRead(): void {
    const readAt = new Date().toISOString();
    this.mentionNotifications.update((existing) =>
      existing.map((item) => (item.readAt ? item : { ...item, readAt }))
    );
  }

  protected toggleStatusDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isStatusDropdownOpen.update((isOpen) => !isOpen);
  }

  protected goToChangePassword(): void {
    this.isProfileMenuOpen.set(false);
    this.openChangePasswordDialog();
  }

  protected goToProfilePage(): void {
    this.isProfileMenuOpen.set(false);
    this.router.navigate(['/settings/profile']);
  }

  protected getCurrentAvailabilityLabel(): string {
    return getUserPresenceLabel(this.getCurrentAvailabilityStatus());
  }

  protected getCurrentAvailabilityOption() {
    const currentStatus = this.getCurrentAvailabilityStatus();
    return this.availabilityOptions.find((option) => option.value === currentStatus) ?? this.availabilityOptions[0];
  }

  protected getCurrentUserStatusClass(baseClass: 'global-profile__status' | 'profile-menu__status'): string {
    return `${baseClass} ${baseClass}--${this.getCurrentAvailabilityStatus()}`;
  }

  protected getAvailabilityDotClass(baseClass: 'profile-menu__status-dot', status: UserAvailabilityStatus): string {
    return `${baseClass} ${baseClass}--${status}`;
  }

  protected isCurrentAvailabilityStatus(status: UserAvailabilityStatus): boolean {
    return this.getCurrentAvailabilityStatus() === status;
  }

  protected onAvailabilityStatusSelected(status: UserAvailabilityStatus): void {
    this.isStatusDropdownOpen.set(false);
    this.isProfileMenuOpen.set(false);

    if (this.getCurrentAvailabilityStatus() === status) {
      return;
    }

    this.currentAvailabilityStatus.set(status);
    this.store.dispatch(UserSetAvailabilityStatusRequest({ status }));
  }

  protected goToManagementPage(): void {
    this.isProfileMenuOpen.set(false);
    this.router.navigate(['/settings/admin']);
  }

  protected canManageUsers(): boolean {
    const role = this.getUserRole();
    return role === 'admin' || role === 'owner' || role === 'superadmin';
  }

  protected canManageCompany(): boolean {
    const role = this.getUserRole();
    return role === 'owner' || role === 'superadmin';
  }

  private getUserRole(): string {
    const user = this.userInfo();
    const userRole = typeof user?.role === 'string' ? user.role.toLowerCase() : '';

    if (userRole) {
      return userRole;
    }

    const attrs = normalizeUserAttributes(user?.attributes ?? null);
    if (!attrs) {
      return '';
    }

    if (attrs['is_owner'] === true || attrs['isOwner'] === true) {
      return 'owner';
    }

    if (attrs['is_admin'] === true || attrs['isAdmin'] === true) {
      return 'admin';
    }

    return typeof attrs['role'] === 'string' ? attrs['role'].toLowerCase() : '';
  }

  protected getCurrentAvailabilityStatus(): UserAvailabilityStatus {
    return this.currentAvailabilityStatus();
  }

  private resolveUserPresenceStatus(userId: string, isOnline: boolean): UserPresenceDisplayStatus {
    const user = this.organizationUsersById()[userId] ?? null;
    const availabilityStatus = normalizeUserAvailabilityStatus(user?.attributes ?? null);
    return toUserPresenceDisplayStatus(availabilityStatus, isOnline);
  }

  private handleTypingStatus(message: {
    channel_id: string;
    sender_id: string;
    name: string;
    attributes: Record<string, string>;
  }): void {
    const currentUserId = this.userInfo()?.id;
    if (!message.channel_id || !message.sender_id || message.sender_id === currentUserId) {
      return;
    }

    if (message.attributes?.['state'] === 'stop') {
      this.clearTypingParticipant(message.channel_id, message.sender_id);
      return;
    }

    this.setTypingParticipant(message.channel_id, message.sender_id, message.name || 'Someone');
  }

  private setTypingParticipant(channelId: string, userId: string, name: string): void {
    this.typingUsersByChannel.update((existing) => ({
      ...existing,
      [channelId]: {
        ...(existing[channelId] ?? {}),
        [userId]: name,
      },
    }));

    this.syncActiveTypingParticipants();
    this.resetTypingExpiryTimer(channelId, userId);
  }

  private clearTypingParticipant(channelId: string, userId: string): void {
    const timerKey = this.typingTimerKey(channelId, userId);
    const existingTimer = this.typingExpiryTimers.get(timerKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
      this.typingExpiryTimers.delete(timerKey);
    }

    this.typingUsersByChannel.update((existing) => {
      const channelUsers = existing[channelId];
      if (!channelUsers || !channelUsers[userId]) {
        return existing;
      }

      const nextChannelUsers = { ...channelUsers };
      delete nextChannelUsers[userId];

      if (Object.keys(nextChannelUsers).length === 0) {
        const nextState = { ...existing };
        delete nextState[channelId];
        return nextState;
      }

      return {
        ...existing,
        [channelId]: nextChannelUsers,
      };
    });

    this.syncActiveTypingParticipants();
  }

  private resetTypingExpiryTimer(channelId: string, userId: string): void {
    const timerKey = this.typingTimerKey(channelId, userId);
    const existingTimer = this.typingExpiryTimers.get(timerKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    this.typingExpiryTimers.set(
      timerKey,
      setTimeout(() => {
        this.clearTypingParticipant(channelId, userId);
      }, 3000)
    );
  }

  private syncActiveTypingParticipants(): void {
    const activeChannelId = this.activeChannelId();
    if (!activeChannelId) {
      this.activeTypingParticipants.set([]);
      return;
    }

    this.activeTypingParticipants.set(
      Object.values(this.typingUsersByChannel()[activeChannelId] ?? {})
    );
  }

  private typingTimerKey(channelId: string, userId: string): string {
    return `${channelId}:${userId}`;
  }

  private clearTypingExpiryTimers(): void {
    this.typingExpiryTimers.forEach((timer) => clearTimeout(timer));
    this.typingExpiryTimers.clear();
  }

  private scheduleUnreadChannelRefresh(): void {
    if (this.channelUnreadRefreshTimer) {
      clearTimeout(this.channelUnreadRefreshTimer);
    }

    this.channelUnreadRefreshTimer = setTimeout(() => {
      this.channelUnreadRefreshTimer = null;
      this.store.dispatch(ChannelFetchRequest());
    }, 250);
  }

  private shouldAffectUnreadCount(messageType: string | null | undefined): boolean {
    switch ((messageType ?? '').trim()) {
      case 'text':
      case 'image':
      case 'video':
      case 'audio':
      case 'file':
        return true;
      default:
        return false;
    }
  }

  protected openChangePasswordDialog(): void {
    this.dialog.open(ChangePasswordModalComponent, {
      width: '520px',
      maxWidth: 'calc(100vw - 24px)',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
    });
  }

  protected async openBugReportDialog(): Promise<void> {
    this.dialog.open(BugReportDialogComponent, {
      width: '760px',
      maxWidth: 'calc(100vw - 24px)',
      maxHeight: 'calc(100dvh - 24px)',
      autoFocus: false,
      restoreFocus: false,
      disableClose: true,
      data: { screenshot: null },
    });
  }

  @HostListener('document:click')
  protected closeProfileMenuOnOutsideClick(): void {
    if (this.isTaskNotificationsOpen()) {
      this.isTaskNotificationsOpen.set(false);
    }
    if (this.isProfileMenuOpen()) {
      this.isStatusDropdownOpen.set(false);
      this.isProfileMenuOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  protected closeProfileMenuOnEscape(): void {
    if (this.isTaskNotificationsOpen()) {
      this.isTaskNotificationsOpen.set(false);
      return;
    }

    if (this.isStatusDropdownOpen()) {
      this.isStatusDropdownOpen.set(false);
      return;
    }

    if (this.isProfileMenuOpen()) {
      this.isProfileMenuOpen.set(false);
      return;
    }

    if (this.isComposingNewChat()) {
      this.isComposingNewChat.set(false);
    }
  }

  private isValidMenuKey(value: string | null): value is AppMenuItem['key'] {
    return value === 'chat' || value === 'tasks' || value === 'todo' || value === 'calendar' || value === 'calls' || value === 'settings';
  }

  private toTimeLabel(dateLike: string): string {
    const value = new Date(dateLike);
    if (Number.isNaN(value.getTime())) {
      return '';
    }

    return value.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: this.getUserTimeZone(),
    });
  }

  private toDayLabel(dateLike: string): string {
    const value = new Date(dateLike);
    if (Number.isNaN(value.getTime())) {
      return '';
    }

    return value.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      timeZone: this.getUserTimeZone(),
    });
  }

  private getUserTimeZone(): string | undefined {
    const timezone = this.userInfo()?.timezone?.trim();
    if (!timezone) {
      return undefined;
    }

    try {
      Intl.DateTimeFormat(undefined, { timeZone: timezone }).format(new Date());
      return timezone;
    } catch {
      return undefined;
    }
  }

  protected logout(): void {
    const dialogRef = this.dialog.open(ConfirmComponent, {
      maxWidth: '400px',
      data: {
        message: 'Are you sure you want to sign out?',
        title: 'Confirm Logout',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.store.dispatch(LogoutRequest());

        this.actions$
          .pipe(ofType(LogoutSuccess), take(1))
          .subscribe(() => {
            this.authStoreService.clear();
            this.router.navigate(['/auth/login']);
          });
      }
    });
  }

  private parseAttachmentSize(value: string | undefined): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
